import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ShoppingCart, Info, Ruler, Check, HelpCircle, X, ZoomIn, Maximize, Box, MessageSquare, Eye, Truck, Sparkles } from 'lucide-react';
import runCartDiagnostics, { emitFailureTelemetry } from './wscCartDiagnostic';

// Bulk pricing tiers — discount enforcement happens in Shopify (Discount rules /
// Shopify Functions / volume-discount app). The configurator displays the live
// preview only and pushes quantity to the product form's quantity input.
const BULK_TIERS = [
  { minQty: 3,  percent: 0.05, shortLabel: '5%',  fullLabel: '5% off'  },
  { minQty: 5,  percent: 0.10, shortLabel: '10%', fullLabel: '10% off' },
  { minQty: 10, percent: 0.15, shortLabel: '15%', fullLabel: '15% off' },
];
const FREE_SHIPPING_MIN_QTY = 12;

// --- Constants & Data ---

// === Kit shipping flat-rate (Shade Screen Solutions ships kits flat at $12) ===
const KIT_SHIPPING_FLAT_RATE = 12.00;

// === Free shipping threshold (12+ items in the cart on either product) ===
const FREE_SHIPPING_QTY = 12;

// === 2026 price increase ===
// Blanket multiplier applied ONLY to the made-to-order, size-based price of the
// ASSEMBLED window screen (base + per-united-inch). It does NOT touch the KIT
// (PRICING_KIT) or the add-on options (mesh, hardware, crossbar, spline tool).
// Set to 1.0 to revert.
// NOTE: this changes only the on-screen ESTIMATE. The real charged price is
// computed by the ACO app and must be raised there too, or the cart total
// shown to the customer at checkout won't match this estimate.
const PRICE_INCREASE = 1.10;

// === ASSEMBLED WSC option-price estimator ===
// Reproduces the ACO pricing table from `aco-option-export-28-05-2026.csv`:
//   • W + H ≤ 51"  → flat $24 (the table's floor)
//   • W + H ≥ 52"  → $25.948 + $0.499 per united-inch above 51
//   • When the shipping box's (length + 2(width + thickness)) ≥ 127" the price
//     jumps to ~$76 + $0.65 per additional inch of length+girth — this is the
//     ACO oversize tier that mirrors carrier additional-handling surcharges.
function estimateAssembledOptionPrice(width, height) {
  const W = Math.max(10, Math.round(width));
  const H = Math.max(10, Math.round(height));
  const sum = W + H;
  if (sum <= 51) return 24 * PRICE_INCREASE;
  let price = 25.948 + (sum - 52) * 0.499;
  const longer = Math.max(W, H);
  const shorter = Math.min(W, H);
  const lengthPlusGirth = (longer + 2) + 2 * ((shorter + 2) + 2); // thickness 2"
  if (lengthPlusGirth >= 127) {
    const jumpPrice = 76 + (lengthPlusGirth - 127) * 0.65;
    price = Math.max(price, jumpPrice);
  }
  return price * PRICE_INCREASE;
}

// === ASSEMBLED shipping cost estimator ===
// Mirrors the actual shipping logic in production:
//   1. Sort screens largest-first, group into boxes of up to 8.
//   2. Box dims = max(W)+2 × max(H)+2 × thickness.
//      thickness: 1–2 screens → 2", 3–4 → 3", 5–8 → 4".  Weight = 7 lb / box.
//   3. Quote FedEx Ground & UPS Ground live; Shopify shows both, customer picks.
//      Final price = max(carrier + $8 padding, $22.50 floor).
//   4. 12+ window screens → free shipping (returns 0).
//   5. LTL fallback at $400 / box when carriers reject as oversized.
//
// Tiers below are calibrated to representative customer-paid rates across
// the destinations in the orders export (`orders_export_1 (1).csv`) and
// the carrier-rule notes (longest side 48 → AH, longest 96 → oversize,
// length + girth thresholds for both FedEx and UPS).
function estimateAssembledShipping(width, height, quantity) {
  if (quantity >= FREE_SHIPPING_QTY) return 0;

  const numBoxes = Math.ceil(quantity / 8);
  const screensInBox = Math.min(quantity, 8);
  const thickness = screensInBox <= 2 ? 2 : screensInBox <= 4 ? 3 : 4;

  const longer = Math.max(width, height);
  const shorter = Math.min(width, height);
  const boxL = longer + 2;
  const boxW = shorter + 2;
  const dimWeight = (boxL * boxW * thickness) / 139; // FedEx/UPS Ground divisor
  const lengthPlusGirth = boxL + 2 * (boxW + thickness);

  let perBox;
  if (boxL > 108 || lengthPlusGirth > 165) {
    perBox = 400.00; // LTL fallback
  } else if (boxL > 65 && boxW > 35) {
    perBox = 220.00; // very large — UPS Large Package + AH surcharges stack
  } else if (boxL > 65) {
    perBox = 90.00;  // long but narrow
  } else if (boxL > 50) {
    perBox = 53.21;  // large; FedEx AH or UPS extended-area
  } else if (boxL > 41 && boxW > 30) {
    perBox = 53.21;  // medium-large (girth+length triggers UPS AH)
  } else if (boxW > 30) {
    perBox = 39.57;  // medium (square-ish boxes)
  } else {
    perBox = 22.50;  // floor
  }

  // Heavier (multi-screen / thicker) boxes scale slightly above the tier base.
  if (dimWeight > 15) {
    perBox += (dimWeight - 15) * 0.30;
  }
  perBox = Math.max(22.50, perBox);
  return perBox * numBoxes;
}

// === Combined Kit vs Assembled savings ===
// Returns the dollar amount a customer saves by buying the kit:
//   • productSavings: assembled total − kit total (positive when kit is cheaper)
//   • shippingSavings: assembled shipping − kit shipping
//   • total: productSavings + shippingSavings
function estimateAssembledSavings({
  width, height, quantity, mesh, hardware, crossbar, kitUnitPrice,
  // Bulk discount applies to the ASSEMBLED window screen only (tier ladder
  // at qty 3/5/10 and free shipping at 12+). Kits don't get any volume
  // discount AND always ship at the $12 flat-pack rate regardless of qty.
  assembledDiscountPercent = 0,
}) {
  const ASSEMBLED_BASE = 22; // Shopify variant base price for the assembled WSC
  const sizeOption = estimateAssembledOptionPrice(width, height);
  const meshPrice = (mesh && PRICING.mesh[mesh]) || 0;
  const hardwarePrice = (hardware && PRICING.hardware[hardware]) || 0;
  const crossbarPrice = crossbar && crossbar !== 'none' ? PRICING.crossbar : 0;
  const assembledUnit = ASSEMBLED_BASE + sizeOption + meshPrice + hardwarePrice + crossbarPrice;
  const qty = Math.max(1, quantity);

  // Assembled gets bulk discount (when applicable) + free shipping at 12+
  const assembledProductTotal = assembledUnit * qty * (1 - assembledDiscountPercent);
  const assembledShipping = estimateAssembledShipping(width, height, quantity);

  // Kit gets NO bulk discount and always pays the $12 flat-pack rate
  const kitProductTotal = kitUnitPrice * qty;
  const kitShipping = KIT_SHIPPING_FLAT_RATE;

  const productSavings = Math.max(0, assembledProductTotal - kitProductTotal);
  // Shipping savings: assembled ships free at 12+, kit is always $12 →
  // at 12+ kit shipping is actually MORE than assembled; cap at 0.
  const shippingSavings = Math.max(0, assembledShipping - kitShipping);
  const assembledShipsFree = quantity >= FREE_SHIPPING_QTY;
  return {
    productSavings,
    shippingSavings,
    total: productSavings + shippingSavings,
    assembledShipsFree,
  };
}

// Dynamic Pricing Logic: Base + (Width + Height) * Factor
// `base` and `perUnitedInch` apply to the ASSEMBLED window screen. The kit
// has its own values — see PRICING_KIT below. We pick the right pair at
// runtime based on the `isKit` flag.
const PRICING = {
  base: 22.00 * PRICE_INCREASE,
  perUnitedInch: 0.28 * PRICE_INCREASE,
  mesh: {
    standard: 0,
    bettervue: 6.00,
    ultravue: 10.00,
    solar: 10.00,
    aluminum: 13.50,
    petscreen: 13.50,
    noseeum: 13.50,
    seevue: 31.50,
  },
  crossbar: 3.50,
  // Kit-only add-on (window screen kit)
  splineTool: 3.50,
  hardware: {
    none: 0,
    springs_h: 2.00,
    springs_w: 2.00,
    ram_h: 2.00,
    ram_w: 2.00,
    swivel_h: 2.00,
    swivel_w: 2.00,
    casement: 5.00,
    plunger2_same_h: 3.00,
    plunger2_same_w: 3.00,
    plunger4_each_h: 6.00,
    plunger4_each_w: 6.00,
    plunger2_each_h: 3.00,
    plunger2_each_w: 3.00,
    butterfly2_each_h: 3.00,
    butterfly2_each_w: 3.00,
    butterfly2_same_w: 3.00,
    butterfly2_same_h: 3.00,
    butterfly4_each_h: 6.00,
    butterfly4_each_w: 6.00,
    wood: 7.50,
  }
};

// Kit-specific pricing — overrides PRICING.base / perUnitedInch when isKit.
// Mirrors the live kit ACO: base $30 + (width + height) × $0.56.
const PRICING_KIT = {
  base: 30.00,
  perUnitedInch: 0.56,
};

const COLORS = {
  white: { name: 'White', hex: '#FFFFFF', border: '#cbd5e1' },
  tan: { name: 'Tan', hex: '#E8DCCC', border: '#c4b49e' },
  silver: { name: 'Silver', hex: '#C8C8D2', border: '#a0a0b0' },
  bronze: { name: 'Bronze', hex: '#3E3330', border: '#2a2220' },
  champagne: { name: 'Champagne', hex: '#BDB8B2', border: '#9a9590' },
  black: { name: 'Black', hex: '#1a1a1a', border: '#000000' },
};

const FRAME_SIZES = [
  { value: '5/16', label: '5/16" x 3/4"', decimal: 0.3125 },
  { value: '3/8', label: '3/8" x 3/4"', decimal: 0.375 },
  { value: '7/16', label: '7/16" x 3/4"', decimal: 0.4375 },
];

const MESH_STYLES = {
  standard: { name: 'PhiferGlass Mesh', opacity: 0.35, color: '#222', pattern: 'grid' },
  bettervue: { name: 'BetterVue Mesh', opacity: 0.25, color: '#222', pattern: 'fine' },
  ultravue: { name: 'UltraVue Mesh', opacity: 0.15, color: '#222', pattern: 'fine' },
  solar: { name: 'Solar Insect Mesh', opacity: 0.75, color: '#111', pattern: 'dense' },
  aluminum: { name: 'Aluminum Mesh', opacity: 0.30, color: '#888', pattern: 'grid' },
  petscreen: { name: 'Pet Screen Mesh', opacity: 0.65, color: '#000', pattern: 'thick' },
  noseeum: { name: 'No-See-Ums (20x20) Mesh', opacity: 0.20, color: '#222', pattern: 'fine' },
  seevue: { name: 'SeeVue Stainless Steel Mesh', opacity: 0.18, color: '#555', pattern: 'fine' },
};

// Available mesh colors per mesh type. Matches the live ACO for both the
// assembled WSC and the kit (both now expose the same color set per mesh).
const MESH_COLORS = {
  standard:  ['charcoal', 'gray'],
  bettervue: ['charcoal'],
  ultravue:  ['charcoal'],
  solar:     ['black'],
  aluminum:  ['charcoal', 'silver', 'black'],
  petscreen: ['black'],
  noseeum:   ['charcoal'],
  seevue:    ['black'],
};

const MESH_COLOR_LABELS = {
  charcoal: 'Charcoal',
  gray: 'Gray',
  silver: 'Silver',
  black: 'Black',
};

// Real-world mesh product images from Shopify CDN
const MESH_IMAGES = {
  standard: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/phiferglass-screen-mesh-screen-mesh-shadescreensolutions-24-inches-wide-x-15-feet-long-charcoal.jpg?v=1579202266',
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/phiferglass-screen-mesh-screen-mesh-shadescreensolutions-24-inches-wide-x-15-feet-long-gray-2.jpg?v=1579202266',
  ],
  bettervue: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/bettervue-screen-mesh-screen-mesh-shadescreensolutions-36-inches-wide-x-15-feet-long-charcoal.jpg?v=1579202311',
  ],
  ultravue: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/ultravue-screen-mesh-screen-mesh-shadescreensolutions.jpg?v=1579202325',
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/ultravue-screen-mesh-screen-mesh-shadescreensolutions-36-inches-wide-x-15-feet-long-charcoal-2.jpg?v=1579202325',
  ],
  solar: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/solar-insect-screen-20x30-mesh-screen-mesh-shadescreensolutions.jpg?v=1579202354',
  ],
  aluminum: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/Aluminum_Black_extertior_480x480.jpg?v=1567661051',
  ],
  petscreen: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/petscreen-mesh-screen-mesh-shadescreensolutions-36-inches-wide-x-100-feet-long-black.jpeg?v=1579202338',
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/petscreen-mesh-screen-mesh-shadescreensolutions-36-inches-wide-x-100-feet-long-gray-2.jpeg?v=1579202338',
  ],
  noseeum: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/products/no-see-um-20x20-mesh-screen-mesh-shadescreensolutions-36-inches-wide-x-100-feet-long-charcoal.jpg?v=1579202554',
  ],
  seevue: [
    'https://cdn.shopify.com/s/files/1/0070/3738/6819/files/PhiferGlass_Interior_e394df18-6def-44bf-90e8-af5b41a85f21_480x480.jpg?v=1567661083',
  ],
};

// placement: 'same' = all on one side, 'each' = split across opposite sides
const HARDWARE_OPTIONS = [
  { value: 'none', label: 'None', price: 0, type: 'none' },
  { value: 'springs_h', label: 'Leaf Tension Springs & Pull Tabs on Sides (Left/Right)', price: 2.00, type: 'spring', side: 'height', count: 2, placement: 'same' },
  { value: 'springs_w', label: 'Leaf Tension Springs & Pull Tabs on Top/Bottom', price: 2.00, type: 'spring', side: 'width', count: 2, placement: 'same' },
  { value: 'ram_h', label: 'Ram Horn Clips on Left or Right', price: 2.00, type: 'ram', side: 'height', count: 2, placement: 'same' },
  { value: 'ram_w', label: 'Ram Horn Clips on Top', price: 2.00, type: 'ram', side: 'width', count: 2, placement: 'same' },
  { value: 'swivel_h', label: '2 Swivel Latches on Left or Right', price: 2.00, type: 'swivel', side: 'height', count: 2, placement: 'same' },
  { value: 'swivel_w', label: '2 Swivel Latches on Top or Bottom', price: 2.00, type: 'swivel', side: 'width', count: 2, placement: 'same' },
  { value: 'plunger2_same_h', label: '2 Plunger Bolts on Left or Right', price: 3.00, type: 'plunger', side: 'height', count: 2, placement: 'same' },
  { value: 'plunger2_same_w', label: '2 Plunger Bolts on Top or Bottom', price: 3.00, type: 'plunger', side: 'width', count: 2, placement: 'same' },
  { value: 'plunger4_each_h', label: '4 Plunger Bolts \u2013 2 on Each Side (Left & Right)', price: 6.00, type: 'plunger', side: 'height', count: 4, placement: 'each' },
  { value: 'plunger4_each_w', label: '4 Plunger Bolts \u2013 2 on Each Top & Bottom', price: 6.00, type: 'plunger', side: 'width', count: 4, placement: 'each' },
  { value: 'plunger2_each_h', label: '2 Plunger Bolts \u2013 1 on Each Side (Left & Right)', price: 3.00, type: 'plunger', side: 'height', count: 2, placement: 'each' },
  { value: 'plunger2_each_w', label: '2 Plunger Bolts \u2013 1 on Each Top & Bottom', price: 3.00, type: 'plunger', side: 'width', count: 2, placement: 'each' },
  { value: 'butterfly2_each_h', label: '2 Butterfly Latches \u2013 1 on Each Side (Left & Right)', price: 3.00, type: 'butterfly', side: 'height', count: 2, placement: 'each' },
  { value: 'butterfly2_each_w', label: '2 Butterfly Latches \u2013 1 on Top & Bottom', price: 3.00, type: 'butterfly', side: 'width', count: 2, placement: 'each' },
  { value: 'butterfly2_same_w', label: '2 Butterfly Latches \u2013 2 on Top or Bottom', price: 3.00, type: 'butterfly', side: 'width', count: 2, placement: 'same' },
  { value: 'butterfly2_same_h', label: '2 Butterfly Latches \u2013 2 on Left or Right', price: 3.00, type: 'butterfly', side: 'height', count: 2, placement: 'same' },
  { value: 'butterfly4_each_h', label: '4 Butterfly Latches \u2013 2 on Each Side (Left & Right)', price: 6.00, type: 'butterfly', side: 'height', count: 4, placement: 'each' },
  { value: 'butterfly4_each_w', label: '4 Butterfly Latches \u2013 2 on Top & Bottom', price: 6.00, type: 'butterfly', side: 'width', count: 4, placement: 'each' },
  { value: 'wood', label: 'Wood Window Hardware \u2013 2 Loop Latches & 2 Top Hangers', price: 7.50, type: 'wood', side: 'topbottom', count: 4, placement: 'each' },
];

const FRACTIONS = [
  { val: 0, label: '0"' },
  { val: 0.0625, label: '1/16"' },
  { val: 0.125, label: '1/8"' },
  { val: 0.1875, label: '3/16"' },
  { val: 0.25, label: '1/4"' },
  { val: 0.3125, label: '5/16"' },
  { val: 0.375, label: '3/8"' },
  { val: 0.4375, label: '7/16"' },
  { val: 0.5, label: '1/2"' },
  { val: 0.5625, label: '9/16"' },
  { val: 0.625, label: '5/8"' },
  { val: 0.6875, label: '11/16"' },
  { val: 0.75, label: '3/4"' },
  { val: 0.8125, label: '13/16"' },
  { val: 0.875, label: '7/8"' },
  { val: 0.9375, label: '15/16"' },
];

const WIDTH_RANGE = { min: 10, max: 48 };
const HEIGHT_RANGE = { min: 10, max: 80 };

// --- Helper Components ---

const RadioCard = ({ selected, onClick, title, price, subtitle, children }) => (
  <div
    onClick={onClick}
    className={`
      cursor-pointer rounded-lg border-2 p-3 transition-all duration-200 relative flex flex-col justify-between
      ${selected ? 'border-[#d44a26] bg-[#d44a26]/5' : 'border-slate-200 hover:border-slate-300 bg-white'}
    `}
  >
    <div className="flex justify-between items-start w-full">
      <div className="flex-1 pr-2">
        <div className={`font-semibold text-sm ${selected ? 'text-[#d44a26]' : 'text-slate-900'}`}>{title}</div>
        {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      </div>
      {selected && <div className="text-[#d44a26] shrink-0"><Check size={16} /></div>}
    </div>
    {price !== undefined && (
      <div className={`text-xs mt-2 font-medium ${price > 0 ? 'text-slate-700' : 'text-green-600'}`}>
        {price === 0 ? 'Included' : `+$${price.toFixed(2)}` }
      </div>
    )}
    {children}
  </div>
);

// --- ACO / WCPA Field Mappings ---
// Each entry maps a configurator state field to the WCPA <select> on the page.
//
// Lookup precedence at runtime (see findAcoSelect):
//   1. selectName  → document.querySelector('select[name="properties[X]"]')
//      Stable across products as long as the merchant uses the same field labels.
//   2. selectId    → document.getElementById(...)  (per-product fallback)
//      WCPA mints a fresh numeric id per field per product; the IDs below are
//      from custom-window-screen-replacements and won't exist on other products.
//
// `values` maps configurator key → the exact <option> text expected by ACO.
// If a kit / new product uses different option labels, override via
// PRODUCT_VALUE_OVERRIDES below — keep this base table as the canonical
// Replacements vocabulary.
const WCPA_MAPPINGS = {
  meshType: {
    selectName: 'properties[Mesh Type]',
    selectId: 'field_select_141475741',
    values: {
      standard:  'PhiferGlass Mesh',
      bettervue: 'BetterVue Mesh',
      ultravue:  'UltraVue Mesh',
      solar:     'Solar Insect Mesh',
      aluminum:  'Aluminum Mesh',
      petscreen: 'Pet Screen Mesh',
      noseeum:   'No-See-Ums (20x20) Mesh',
      seevue:    'SeeVue Stainless Steel Mesh',
    }
  },
  meshColor: {
    selectName: 'properties[Mesh Color]',
    selectId: 'field_select_519020549',
    values: {
      charcoal: 'Charcoal',
      gray: 'Gray',
      silver: 'Silver',
      black: 'Black',
    }
  },
  frameThickness: {
    selectName: 'properties[Frame Thickness]',
    selectId: 'field_select_581831971',
    values: {
      '5/16': '5/16 x 3/4',
      '3/8':  '3/8 x 3/4',
      '7/16': 'Option - 3', // ACO uses 'Option - 3' as the value for 7/16 x 3/4
    }
  },
  frameColor: {
    selectName: 'properties[Frame Color]',
    selectId: 'field_select_819157818',
    values: {
      white:     'White',
      tan:       'Tan',
      silver:    'Silver',
      bronze:    'Bronze',
      champagne: 'Champagne',
      black:     'Black',
    }
  },
  width: {
    selectName: 'properties[Width]',
    selectId: 'field_select_887142794',
  },
  widthFraction: {
    selectName: 'properties[Width Fraction]',
    selectId: 'field_select_983805809',
  },
  height: {
    selectName: 'properties[Height]',
    selectId: 'field_select_29970121',
  },
  heightFraction: {
    selectName: 'properties[Height Fraction]',
    selectId: 'field_select_198609809',
  },
  hardware: {
    selectName: 'properties[Hardware]',
    selectId: 'field_select_155614663',
    values: {
      none:              'None',
      springs_h:         'Leaf Tension Springs & Pull Tabs on Height Side',
      springs_w:         'Leaf Tension Springs & Pull Tabs on Width Side',
      ram_h:             'Ram Horn Clips on Height Side',
      ram_w:             'Ram Horn Clips on Width Side',
      swivel_h:          '2 Swivel Latches on Height Side',
      swivel_w:          '2 Swivel Latches on Width Side',
      casement:          'Casement Clips (4 Per Screen)',
      plunger2_same_h:   '2 Plunger Bolts on same height side',
      plunger2_same_w:   '2 Plunger Bolts on same width side',
      plunger4_each_h:   '4 Plunger Bolts - 2 on each height side',
      plunger4_each_w:   '4 Plunger Bolts - 2 on each width side',
      plunger2_each_h:   '2 Plunger Bolts - 1 on each height side',
      plunger2_each_w:   '2 Plunger Bolts - 1 on each width side',
      butterfly2_each_h: '2 Butterfly Latch - 1 on each height side',
      butterfly2_each_w: '2 Butterfly Latch - 1 on each width side',
      butterfly2_same_w: '2 Butterfly Latch - 2 on same width side',
      butterfly2_same_h: '2 Butterfly Latch - 2 on same height side',
      butterfly4_each_h: '4 Butterfly Latch - 2 on each height side',
      butterfly4_each_w: '4 Butterfly Latch - 2 on each short side',
      wood:              'Wood Window Hardware - 2 Loop Latches w/ 2 Top Hangers',
    }
  },
  crossbar: {
    selectName: 'properties[Crossbar]',
    selectId: 'field_select_712098797',
    values: {
      none:   '',
      height: 'Crossbar Parallel to Height',
      width:  'Crossbar Parallel to Width',
    }
  },
  // Kit-only: written when isKit (ACO field IDs live in PRODUCT_FIELD_OVERRIDES)
  splineTool: {
    selectName: 'properties[Include Spline Roller Tool?]',
    selectId: '',
    values: {
      'true':  'Yes',
      'false': 'No',
    }
  },
};

// Per-product ACO field-ID overrides. Each product's WCPA form has its own
// element IDs even when the labels/values are identical. Adding a handle here
// switches the configurator's ACO lookup to those IDs (other lookup paths —
// selectName + fallback — still run for safety).
const PRODUCT_FIELD_OVERRIDES = {
  'custom-window-screen-kit': {
    meshType:       'field_select_674574883',
    meshColor:      'field_select_662436017',
    frameThickness: 'field_select_533555130',
    frameColor:     'field_select_210225471',
    width:          'field_select_952766846',
    widthFraction:  'field_select_544801708',
    height:         'field_select_45482222',
    heightFraction: 'field_select_719607019',
    hardware:       'field_select_383990696',
    crossbar:       'field_select_962564037',
    splineTool:     'field_select_3234871045',
  },
};

// --- Per-product value overrides ---
// Different products configured in ACO/WCPA may use different <option> text for
// the same logical choice. Keys here override the corresponding WCPA_MAPPINGS
// entries' `values` for that product handle. Verify against the live form
// (see logAcoFormStructure() console output) before adding entries.
const PRODUCT_VALUE_OVERRIDES = {
  'custom-window-screen-kit': {
    // Kit ACO uses uppercase X in "No-See-Ums (20X20) Mesh" (assembled uses
    // lowercase x). Exact-string match fails without this override.
    meshType: { noseeum: 'No-See-Ums (20X20) Mesh' },
    // Frame Thickness values match the assembled vocabulary already
    // (5/16 x 3/4, 3/8 x 3/4, "Option - 3" for 7/16 x 3/4) — no override needed.
  },
};

// Resolve the effective value table for a given mapping key on this product.
function resolveAcoValues(mappingKey) {
  const handle =
    (typeof window !== 'undefined' && window.WSC_CONFIG && window.WSC_CONFIG.productHandle) || '';
  const overrides = PRODUCT_VALUE_OVERRIDES[handle];
  const base = WCPA_MAPPINGS[mappingKey].values;
  if (!base || !overrides || !overrides[mappingKey]) return base;
  return { ...base, ...overrides[mappingKey] };
}

// For WCPA fields that render as react-select (`wcpa_advanced_select`,
// `dropdown_type: "without_search"`), the hidden input + JSON updates aren't
// enough — WCPA's React app keeps internal state and recalculates from it,
// not from the DOM. Calling react-select's `onChange` prop via the React
// fiber on the DOM element is the only way to update its real state.
//
// Walks up the DOM from `containerEl`, looking on each element for React
// 16+'s `__reactProps$*` key. The first element that has both `onChange` AND
// an `options` array is react-select's Select component. We find the option
// matching `value` and invoke onChange the same way react-select would when
// the user clicks an option.
function invokeReactSelectChange(containerEl, value) {
  if (!containerEl) return false;
  let el = containerEl;
  for (let depth = 0; depth < 10 && el && el !== document.body; depth++) {
    const propsKey = Object.keys(el).find(k => k.startsWith('__reactProps$'));
    if (propsKey) {
      const props = el[propsKey];
      if (props && typeof props.onChange === 'function' &&
          Array.isArray(props.options) && props.options.length) {
        const opt = props.options.find(o =>
          o && (o.value === value || o.label === value)
        );
        if (opt) {
          try {
            props.onChange(opt, { action: 'select-option', name: props.name });
            return true;
          } catch (e) {
            console.warn('[WSC] react-select onChange invocation failed:', e);
          }
        }
      }
    }
    el = el.parentElement;
  }
  return false;
}

// Find an ACO field for a given mapping key. Lookup order:
//   1. PRODUCT_FIELD_OVERRIDES[productHandle][mappingKey] (kit-specific IDs)
//   2. select[name="properties[…]"]                       (theme-agnostic)
//   3. mapping.selectId                                    (default ID)
function findAcoSelect(mappingKey) {
  if (typeof document === 'undefined') return null;
  const mapping = WCPA_MAPPINGS[mappingKey];
  if (!mapping) return null;

  const handle =
    (typeof window !== 'undefined' && window.WSC_CONFIG && window.WSC_CONFIG.productHandle) || '';

  // 1. Product-specific override
  if (handle && PRODUCT_FIELD_OVERRIDES[handle] && PRODUCT_FIELD_OVERRIDES[handle][mappingKey]) {
    const byOverride = document.getElementById(PRODUCT_FIELD_OVERRIDES[handle][mappingKey]);
    if (byOverride) return byOverride;
  }

  // 2. By name
  if (mapping.selectName) {
    const byName = document.querySelector(`select[name="${mapping.selectName}"]`);
    if (byName) return byName;
  }

  // 3. By default ID
  if (mapping.selectId) {
    const byId = document.getElementById(mapping.selectId);
    if (byId) return byId;
  }

  return null;
}

// One-shot diagnostic — dumps the WCPA form so missing fields / mismatched
// option text are obvious in the browser console. Logged once per page load.
let _wscFormStructureLogged = false;
function logAcoFormStructure() {
  if (_wscFormStructureLogged) return;
  _wscFormStructureLogged = true;
  const wcpaForms = document.querySelectorAll('.wcpa_form_outer, .wcpa-form-fields, [class*="wcpa"]');
  const allSelects = new Set();
  wcpaForms.forEach((form) => form.querySelectorAll('select').forEach((s) => allSelects.add(s)));
  if (allSelects.size === 0) {
    document.querySelectorAll('select[name^="properties["]').forEach((s) => allSelects.add(s));
  }
  const summary = Array.from(allSelects).map((s) => ({
    name: s.name,
    id: s.id,
    options: Array.from(s.options).map((o) => o.value),
  }));
  console.groupCollapsed(`[WSC] WCPA form discovered (${summary.length} <select> fields)`);
  summary.forEach((f) => console.log(`${f.name || '(no name)'}  #${f.id}`, f.options));
  console.log('Mapping resolution:');
  Object.keys(WCPA_MAPPINGS).forEach((k) => {
    const mapping = WCPA_MAPPINGS[k];
    const found = findAcoSelect(k);
    let label;
    if (found) {
      label = `${found.name || found.id} ✓`;
    } else if (mapping.selectName) {
      // No visible widget — check if the hidden input exists. WCPA renders
      // hidden inputs for every field by default, so this is the source of
      // truth for cart submission and is what setMappedValue writes to.
      const hidden = document.querySelector(`input[type="hidden"][name="${mapping.selectName}"]`);
      label = hidden
        ? `${mapping.selectName} ✓ (via hidden input)`
        : 'NOT FOUND ✗';
    } else {
      label = 'NOT FOUND ✗';
    }
    console.log(`  ${k.padEnd(16)} → ${label}`);
  });
  console.groupEnd();
}

// --- Hardware Overlay Image Mapping ---
// Hardware piece data extracted from Shopify CDN overlay images (832x900 canvas)
// Each piece is individually cropped with position relative to frame area (0-1 scale)
// cx/cy = center of piece as fraction of frame width/height
// fw/fh = piece size as fraction of frame width/height
// x/y/w/h = bounding box in original 832x900 canvas (for clip-path calculation)
const CDN_BASE = 'https://cdn.shopify.com/s/files/1/0070/3738/6819/files';
// Original canvas dimensions and frame boundaries
const CDN_CANVAS = { w: 832, h: 900 };
const CDN_FRAME = { left: 221, top: 89, right: 611, bottom: 568 }; // frame in canvas coords (measured from live site canvas)

const HARDWARE_IMAGE_MAP = {
  none: null,
  springs_h: `${CDN_BASE}/S1-Leaf_Tension_Springs___Pull_Tabs_on_Long_Side.png`,
  springs_w: `${CDN_BASE}/S1-Leaf_Tension_Springs___Pull_Tabs_on_Short_Side.png`,
  ram_h: `${CDN_BASE}/S1-Ram_Horn_Clips_on_Long_Side.png`,
  ram_w: `${CDN_BASE}/S1-Ram_Horn_Clips_on_Short_Side.png`,
  swivel_h: `${CDN_BASE}/S1-2_Acorn_Latches_on_Long_Side.png`,
  swivel_w: `${CDN_BASE}/S1-2_Acorn_Latches_on_Short_Side.png`,
  casement: `${CDN_BASE}/S1-Casement_Clips_4_Per_Screen.png`,
  plunger2_same_h: `${CDN_BASE}/S1-2_Plunger_Bolts_on_same_long_side.png`,
  plunger2_same_w: `${CDN_BASE}/S1-2_Plunger_Bolts_on_same_short_side.png`,
  plunger4_each_h: `${CDN_BASE}/S1-4_Plunger_Bolts_-_2_on_each_long_side.png`,
  plunger4_each_w: `${CDN_BASE}/S1-4_Plunger_Bolts_-_2_on_each_short_side.png`,
  plunger2_each_h: `${CDN_BASE}/S1-2_Plunger_Bolts_-_1_on_each_long_side.png`,
  plunger2_each_w: `${CDN_BASE}/S1-2_Plunger_Bolts_-_1_on_each_short_side.png`,
  butterfly2_each_h: `${CDN_BASE}/S1-2_Butterfly_Latch_-_1_on_each_long_side.png`,
  butterfly2_each_w: `${CDN_BASE}/S1-2_Butterfly_Latch_-_1_on_each_short_side.png`,
  butterfly2_same_w: `${CDN_BASE}/S1-2_Butterfly_Latch_-_2_on_same_short_side.png`,
  butterfly2_same_h: `${CDN_BASE}/S1-2_Butterfly_Latch_-_2_on_same_long_side.png`,
  butterfly4_each_h: `${CDN_BASE}/S1-4_Butterfly_Latch_-_2_on_each_long_side.png`,
  butterfly4_each_w: `${CDN_BASE}/S1-4_Butterfly_Latch_-_2_on_each_short_side.png`,
  wood: `${CDN_BASE}/S1-Wood_Window_Hardware_-_2_Loop_Latches_w_2_Top_Hangers.png`,
};

// Hardware piece crop data (bounding boxes in 832x900 CDN canvas) + edge-anchored placement.
// x,y,w,h = crop rectangle in CDN canvas pixels.
// edge = which frame edge this piece attaches to ('top'|'bottom'|'left'|'right').
// pos = fractional position (0-1) along that edge where the piece center sits.
//   For top/bottom edges: 0=left end, 1=right end.
//   For left/right edges: 0=top end, 1=bottom end.
const HARDWARE_PIECES = {
  // Leaf tension springs & pull tabs — positioned ~1/3 from each end
  // Leaf springs (left) sit OUTSIDE the frame; pull tabs (right) sit INSIDE the frame
  springs_h: [
    { x:202, y:109, w:19, h:86, edge:'left',  pos:0.18, inset: -20 },
    { x:571, y:154, w:26, h:31, edge:'right', pos:0.30, inset: 10 },
    { x:202, y:379, w:19, h:86, edge:'left',  pos:0.82, inset: -20 },
    { x:571, y:384, w:26, h:31, edge:'right', pos:0.70, inset: 10 },
  ],
  // Leaf springs (top) sit OUTSIDE the frame; pull tabs (bottom) sit INSIDE the frame
  springs_w: [
    { x:237, y:71,  w:86, h:19, edge:'top',    pos:0.18, inset: -20 },
    { x:507, y:71,  w:86, h:19, edge:'top',    pos:0.82, inset: -20 },
    { x:287, y:440, w:31, h:26, edge:'bottom', pos:0.30, inset: 10 },
    { x:517, y:440, w:31, h:26, edge:'bottom', pos:0.70, inset: 10 },
  ],
  ram_h: [
    { x:214, y:131, w:25, h:40, edge:'left', pos:0.129 },
    { x:214, y:401, w:25, h:40, edge:'left', pos:0.871 },
  ],
  ram_w: [
    { x:263, y:81, w:40, h:25, edge:'top', pos:0.159 },
    { x:533, y:81, w:40, h:25, edge:'top', pos:0.851 },
  ],
  swivel_h: [
    { x:195, y:167, w:42, h:25, edge:'left', pos:0.193, inset: -10 },
    { x:195, y:347, w:42, h:25, edge:'left', pos:0.807, inset: -10 },
  ],
  swivel_w: [
    { x:285, y:62, w:25, h:42, edge:'top', pos:0.199, inset: -10 },
    { x:545, y:62, w:25, h:42, edge:'top', pos:0.865, inset: -10 },
  ],
  casement: [
    { x:191, y:140, w:23, h:18, edge:'left',  pos:0.125 },
    { x:618, y:140, w:24, h:18, edge:'right', pos:0.125 },
    { x:190, y:430, w:24, h:18, edge:'left',  pos:0.875 },
    { x:618, y:430, w:24, h:18, edge:'right', pos:0.875 },
  ],
  plunger2_same_h: [
    { x:574, y:135, w:58, h:13, edge:'right', pos:0.110 },
    { x:574, y:406, w:58, h:13, edge:'right', pos:0.890 },
  ],
  plunger2_same_w: [
    { x:272, y:68, w:14, h:58, edge:'top', pos:0.149 },
    { x:543, y:68, w:14, h:58, edge:'top', pos:0.844 },
  ],
  plunger4_each_h: [
    { x:201, y:139, w:58, h:13, edge:'left',  pos:0.118 },
    { x:572, y:139, w:58, h:13, edge:'right', pos:0.118 },
    { x:201, y:410, w:58, h:13, edge:'left',  pos:0.882 },
    { x:572, y:410, w:58, h:13, edge:'right', pos:0.882 },
  ],
  plunger4_each_w: [
    { x:274, y:70,  w:14, h:55, edge:'top',    pos:0.154 },
    { x:545, y:70,  w:14, h:55, edge:'top',    pos:0.849 },
    { x:273, y:447, w:14, h:49, edge:'bottom', pos:0.151 },
    { x:544, y:447, w:14, h:49, edge:'bottom', pos:0.846 },
  ],
  plunger2_each_h: [
    { x:201, y:268, w:58, h:14, edge:'left',  pos:0.388 },
    { x:573, y:268, w:58, h:14, edge:'right', pos:0.388 },
  ],
  plunger2_each_w: [
    { x:413, y:74,  w:14, h:50, edge:'top',    pos:0.510 },
    { x:412, y:447, w:14, h:52, edge:'bottom', pos:0.508 },
  ],
  butterfly2_each_h: [
    { x:212, y:248, w:39, h:32, edge:'left',  pos:0.365 },
    { x:581, y:248, w:39, h:32, edge:'right', pos:0.365 },
  ],
  butterfly2_each_w: [
    { x:404, y:80,  w:32, h:39, edge:'top',    pos:0.510 },
    { x:404, y:451, w:32, h:39, edge:'bottom', pos:0.510 },
  ],
  butterfly2_same_w: [
    { x:264, y:80, w:32, h:39, edge:'top', pos:0.151 },
    { x:534, y:80, w:32, h:39, edge:'top', pos:0.844 },
  ],
  butterfly2_same_h: [
    { x:211, y:138, w:39, h:32, edge:'left', pos:0.136 },
    { x:211, y:408, w:39, h:32, edge:'left', pos:0.864 },
  ],
  butterfly4_each_h: [
    { x:212, y:148, w:39, h:32, edge:'left',  pos:0.157 },
    { x:581, y:148, w:39, h:32, edge:'right', pos:0.157 },
    { x:212, y:398, w:39, h:32, edge:'left',  pos:0.843 },
    { x:581, y:398, w:39, h:32, edge:'right', pos:0.843 },
  ],
  butterfly4_each_w: [
    { x:264, y:80,  w:32, h:39, edge:'top',    pos:0.151 },
    { x:534, y:80,  w:32, h:39, edge:'top',    pos:0.844 },
    { x:264, y:451, w:32, h:39, edge:'bottom', pos:0.151 },
    { x:534, y:451, w:32, h:39, edge:'bottom', pos:0.844 },
  ],
  wood: [
    { x:181, y:100, w:24, h:63, edge:'top',    pos:-0.072 },
    { x:630, y:100, w:24, h:63, edge:'top',    pos:1.079 },
    { x:281, y:425, w:32, h:41, edge:'bottom', pos:0.195 },
    { x:521, y:425, w:32, h:41, edge:'bottom', pos:0.810 },
  ],
};


// --- URL-safe base64 helpers for the `?build=...` hydration / cart-edit flow ---
function wscEncodeBuild(state) {
  try {
    return btoa(unescape(encodeURIComponent(JSON.stringify(state))))
      .replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
  } catch (e) { return ''; }
}
function wscDecodeBuild(encoded) {
  if (!encoded || typeof encoded !== 'string') return null;
  try {
    let s = encoded.replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    return JSON.parse(decodeURIComponent(escape(atob(s))));
  } catch (e) { return null; }
}

// --- Main Component ---

export default function App({ embedded = false, kit = false }) {
  // Detect Shopify embedding: either explicit prop or presence of WSC_CONFIG on window
  const isEmbedded = embedded || (typeof window !== 'undefined' && !!window.WSC_CONFIG);
  const isKit = kit || (typeof window !== 'undefined' && !!(window.WSC_CONFIG && window.WSC_CONFIG.kit));

  // State
  const [mesh, setMesh] = useState('standard');
  const [meshColor, setMeshColor] = useState('charcoal'); // Charcoal or Gray (matches live store)
  const [frameSize, setFrameSize] = useState('5/16');
  const [color, setColor] = useState('white');
  const [width, setWidth] = useState(10);
  const [widthFraction, setWidthFraction] = useState(0);
  const [height, setHeight] = useState(14);
  const [heightFraction, setHeightFraction] = useState(0);
  const [hardware, setHardware] = useState('none');
  const [crossbar, setCrossbar] = useState('none'); // none, width, height
  const [comments, setComments] = useState('');
  const [quantity, setQuantity] = useState(1);
  // Kit-only: spline roller tool add-on. Default included; customer can opt out.
  const [splineTool, setSplineTool] = useState(true);

  // Edit-from-cart support: when arriving via /cart's Edit link, hydrate from
  // ?build=... and remember the original cart line key so we can replace it
  // after the customer adds the edited build.
  const [editLineKey, setEditLineKey] = useState(null);
  const [restoredFromEdit, setRestoredFromEdit] = useState(false);
  
  // View State: 'full' or 'corner'
  const [viewMode, setViewMode] = useState('full');
  
  // UI Focus State
  const [highlightComments, setHighlightComments] = useState(false);
  const commentsRef = useRef(null);
  const previewColumnRef = useRef(null);
  const previewInnerRef = useRef(null);
  const orderSummaryRef = useRef(null);
  const previewWrapRef = useRef(null);

  // Lightbox State
  const [lightboxImg, setLightboxImg] = useState(null);

  // Preview Modal — mobile-only floating "Preview My Screen" button + modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [isPreviewOutOfView, setIsPreviewOutOfView] = useState(false);
  const previewCardRef = useRef(null); // the Live Preview card itself

  // Track viewport width for mobile-only preview scaling.
  // On mobile (<1024px viewport) the preview is full-width and may overflow for wide screens.
  // On desktop the preview column is narrow but the SVG already fits — no scaling needed.
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );
  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Mobile-only: track when the Live Preview card scrolls out of view
  useEffect(() => {
    const el = previewCardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setIsPreviewOutOfView(!entry.isIntersecting),
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Auto-switch mesh color when mesh type changes and current color isn't available
  useEffect(() => {
    const available = MESH_COLORS[mesh] || ['charcoal'];
    if (!available.includes(meshColor)) {
      setMeshColor(available[0]);
    }
  }, [mesh, meshColor]);

  // Kit doesn't offer Aluminum or SeeVue. If we hydrated from a saved build
  // that picked one of those, fall back to the default.
  useEffect(() => {
    if (isKit && (mesh === 'aluminum' || mesh === 'seevue')) {
      setMesh('standard');
    }
  }, [isKit, mesh]);

  // Hydrate from `?build=...` (and `?edit_line=...`) — supports the cart's
  // Edit-this-line workflow. Run once on mount.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const editKey = params.get('edit_line');
      if (editKey) setEditLineKey(editKey);

      // Restore quantity from the URL `?qty=` param (set by the cart's Edit
      // link from the cart line's current quantity). Falls back to the
      // encoded build's quantity if present, else stays at 1.
      const qtyParam = parseInt(params.get('qty'), 10);
      if (!Number.isNaN(qtyParam) && qtyParam >= 1) {
        setQuantity(Math.min(99, qtyParam));
      }

      const encoded = params.get('build');
      if (!encoded) return;
      const d = wscDecodeBuild(encoded);
      if (!d) return;
      if (d.mesh) setMesh(d.mesh);
      if (d.meshColor) setMeshColor(d.meshColor);
      if (d.frameSize) setFrameSize(d.frameSize);
      if (d.color) setColor(d.color);
      if (typeof d.width === 'number') setWidth(d.width);
      if (typeof d.widthFraction === 'number') setWidthFraction(d.widthFraction);
      if (typeof d.height === 'number') setHeight(d.height);
      if (typeof d.heightFraction === 'number') setHeightFraction(d.heightFraction);
      if (d.hardware) setHardware(d.hardware);
      if (d.crossbar) setCrossbar(d.crossbar);
      if (typeof d.comments === 'string') setComments(d.comments);
      if (typeof d.splineTool === 'boolean') setSplineTool(d.splineTool);
      // Fallback: if URL didn't have qty but the encoded build does, use it
      if (Number.isNaN(qtyParam) && typeof d.quantity === 'number' && d.quantity >= 1) {
        setQuantity(Math.min(99, d.quantity));
      }
      setRestoredFromEdit(true);
      setTimeout(() => setRestoredFromEdit(false), 6000);
    } catch (e) { /* ignore */ }
  }, []);

  // --- ACO / WCPA Field Sync ---
  // Detects hidden ACO fields on the Shopify product page and syncs configurator state to them.
  const [acoReady, setAcoReady] = useState(false);

  useEffect(() => {
    // Poll for ACO fields to appear in the DOM. Use the resilient name+id
    // lookup so this works on any product whose WCPA form follows the
    // window-screen field-name schema, not just the original Replacements page.
    const checkAco = () => {
      const firstField = findAcoSelect('meshColor');
      if (firstField) {
        console.log('[WSC] ACO fields detected, ready to sync');
        logAcoFormStructure();
        setAcoReady(true);
        return true;
      }
      return false;
    };
    if (checkAco()) return;
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (checkAco() || attempts >= 20) {
        clearInterval(interval);
        if (attempts >= 20) {
          console.log('[WSC] ACO fields not found after 10s — running standalone');
          // Still dump whatever WCPA selects exist so the merchant can see
          // what field names the live form actually uses on this product.
          logAcoFormStructure();
        }
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // --- Add-to-Cart failure capture / diagnostics ---
  // When an Add-to-Cart click can't complete (acoReady never flipped, or the
  // theme submit button is missing) the button used to silently do nothing —
  // so nobody could tell WHY a customer "couldn't add to cart". We now run a
  // read-only audit of the whole chain, record it, and surface a message.
  const [cartError, setCartError] = useState(null);

  // Snapshot of everything the diagnostic needs to audit the live chain,
  // including the CURRENT selection mapped to the ACO option text it expects
  // (so option/label drift is caught).
  const buildDiagCtx = () => ({
    acoReady,
    isKit,
    productHandle:
      (typeof window !== 'undefined' && window.WSC_CONFIG && window.WSC_CONFIG.productHandle) || '',
    findField: findAcoSelect,
    mappings: WCPA_MAPPINGS,
    expectedValues: {
      meshType:       resolveAcoValues('meshType')[mesh],
      meshColor:      resolveAcoValues('meshColor')[meshColor] || meshColor,
      frameThickness: resolveAcoValues('frameThickness')[frameSize],
      frameColor:     resolveAcoValues('frameColor')[color],
      hardware:       resolveAcoValues('hardware')[hardware] || 'None',
      crossbar:       resolveAcoValues('crossbar')[crossbar] || '',
      ...(isKit ? { splineTool: splineTool ? 'Yes' : 'No' } : {}),
    },
  });

  const captureCartFailure = (trigger) => {
    let report = null;
    try {
      report = runCartDiagnostics({ ...buildDiagCtx(), trigger });
    } catch (e) {
      console.error('[WSC] diagnostic threw:', e);
    }
    const record = {
      trigger,
      at: new Date().toISOString(),
      handle: (typeof window !== 'undefined' && window.WSC_CONFIG && window.WSC_CONFIG.productHandle) || '',
      url: typeof location !== 'undefined' ? location.href : '',
      acoReady,
      verdict: report ? report.verdict : null,
    };
    if (typeof window !== 'undefined') {
      window.__WSC_CART_FAILURES = window.__WSC_CART_FAILURES || [];
      window.__WSC_CART_FAILURES.push(record);
    }
    // Auto-report to the store's analytics (GA4 / GTM / Clarity) and an
    // optional first-party beacon, so we SEE these failures from real
    // customers without asking them to do anything.
    try { emitFailureTelemetry(report, { acoReady, isKit }); } catch (e) { /* noop */ }
    console.error('[WSC] Add-to-Cart did not complete:', record);
    setCartError(
      (report && report.verdict && report.verdict.userMessage) ||
      'Something went wrong adding this to your cart. Please reload the page and try again.'
    );
    return report;
  };

  // Expose the diagnostic on the live page so it can be run by hand
  // (window.WSC_DIAGNOSE()) or shared with a customer who's stuck — no
  // pasting required. Rebinds when the selection changes so the option-drift
  // check always reflects the current configuration.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.WSC_DIAGNOSE = (opts = {}) => runCartDiagnostics({ ...buildDiagCtx(), ...opts });
  }, [acoReady, mesh, meshColor, frameSize, color, width, widthFraction, height, heightFraction, hardware, crossbar, isKit, splineTool]);

  // Sync all configurator state to ACO fields whenever anything changes
  useEffect(() => {
    if (!acoReady) return;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    const setMappedValue = (mappingKey, value) => {
      const mapping = WCPA_MAPPINGS[mappingKey];
      if (!mapping) return;

      // 1. Locate the visible element (may be a native <select> OR a
      //    react-select <div> for wcpa_advanced_select fields). May not
      //    exist yet if the react-select is still mounting.
      const select = findAcoSelect(mappingKey);

      // 2. Locate the hidden input — this is what Shopify reads on submit,
      //    and it's rendered by WCPA's Liquid so it's available immediately
      //    on page load regardless of whether the visible widget has mounted.
      let hidden = mapping.selectName
        ? document.querySelector(`input[type="hidden"][name="${mapping.selectName}"]`)
        : null;
      if (!hidden && select && select.id) {
        const numericId = select.id.replace(/^field_/, '');
        hidden = document.getElementById(`${numericId}_hidden`);
      }

      if (!select && !hidden) {
        console.warn(
          `[WSC] sync: no element found for "${mappingKey}" (looked for ` +
          `name="${mapping.selectName || '(none)'}" / id="${(mapping.selectId || '')}")`
        );
        return;
      }

      // 3a. If the visible element is a real <select>, update it so the
      //     dropdown UI reflects the choice. WCPA's listeners will handle
      //     the rest (price recalc + _acpaHidden sync).
      if (select && select.tagName === 'SELECT' && select.options) {
        const match = Array.from(select.options).find((o) => o.value === value);
        if (match) {
          select.focus({ preventScroll: true });
          select.value = match.value;
          setTimeout(() => {
            select.dispatchEvent(new Event('change', { bubbles: true }));
            select.blur();
            window.scrollTo(scrollX, scrollY);
          }, 0);
        } else {
          console.warn(
            `[WSC] sync: option "${value}" not found on ${select.name || select.id}.`,
            'Available:', Array.from(select.options).map((o) => o.value)
          );
        }
      }
      // 3b. If the visible element is a react-select (wcpa_advanced_select),
      //     invoke its onChange via the React fiber so WCPA's internal state
      //     updates. This is the ONLY path that triggers price recalc for
      //     react-select fields like the kit's Mesh Type.
      else if (select && select.classList && select.classList.contains('wcpa_advanced_select')) {
        const ok = invokeReactSelectChange(select, value);
        if (!ok) {
          console.warn(
            `[WSC] react-select sync for "${mappingKey}" couldn't find an ` +
            `onChange handler on the React fiber. Value "${value}" was ` +
            `still written to the hidden input + _acpaHidden, but WCPA ` +
            `may not see the change until the form is re-rendered.`
          );
        }
      }

      // 4. Update the per-field hidden input.
      if (hidden && hidden.value !== value) {
        hidden.value = value;
        hidden.dispatchEvent(new Event('input', { bubbles: true }));
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // 5. Update the `_acpaHidden` JSON — WCPA stores its state-of-record
      //    here and recalculates price + re-syncs per-field hidden inputs
      //    from this blob on submit. Without this, our change to the
      //    per-field hidden input gets overwritten and price never updates.
      const fieldKeyForJson =
        (hidden && hidden.id ? hidden.id.replace(/_hidden$/, '') : null) ||
        (select && select.id ? select.id.replace(/^field_/, '') : null);
      if (fieldKeyForJson) {
        const acpaHiddenInput = document.querySelector('input[name="properties[_acpaHidden]"]');
        if (acpaHiddenInput) {
          try {
            const data = JSON.parse(acpaHiddenInput.value || '{}');
            if (!data[fieldKeyForJson]) data[fieldKeyForJson] = {};
            data[fieldKeyForJson].value = value;
            data[fieldKeyForJson]._value = value === '' || value == null ? [] : [value];
            acpaHiddenInput.value = JSON.stringify(data);
          } catch (e) {
            console.warn(`[WSC] Could not update _acpaHidden for "${mappingKey}":`, e);
          }
        }
      }
    };

    // Format fraction for ACO (0, 1/16, 1/8 ... 15/16)
    const FRAC_MAP = {
      0: '0', 0.0625: '1/16', 0.125: '1/8', 0.1875: '3/16',
      0.25: '1/4', 0.3125: '5/16', 0.375: '3/8', 0.4375: '7/16',
      0.5: '1/2', 0.5625: '9/16', 0.625: '5/8', 0.6875: '11/16',
      0.75: '3/4', 0.8125: '13/16', 0.875: '7/8', 0.9375: '15/16',
    };

    const meshTypeValues   = resolveAcoValues('meshType');
    const meshColorValues  = resolveAcoValues('meshColor');
    const frameThickValues = resolveAcoValues('frameThickness');
    const frameColorValues = resolveAcoValues('frameColor');
    const hardwareValues   = resolveAcoValues('hardware');
    const crossbarValues   = resolveAcoValues('crossbar');

    setMappedValue('meshType',       meshTypeValues[mesh]);
    setMappedValue('meshColor',      meshColorValues[meshColor] || meshColor);
    setMappedValue('frameThickness', frameThickValues[frameSize]);
    setMappedValue('frameColor',     frameColorValues[color]);
    setMappedValue('width',          String(width));
    setMappedValue('widthFraction',  FRAC_MAP[widthFraction] || '0');
    setMappedValue('height',         String(height));
    setMappedValue('heightFraction', FRAC_MAP[heightFraction] || '0');
    setMappedValue('hardware',       hardwareValues[hardware] || 'None');
    setMappedValue('crossbar',       crossbarValues[crossbar] || '');

    // Kit-only field — write the spline-tool selection to ACO. The kit ACO
    // marks this field as required, so the customer can't reach checkout
    // unless we sync it.
    if (isKit) {
      setMappedValue('splineTool', splineTool ? 'Yes' : 'No');
    }

    // NOTE: comments are synced in a separate debounced effect below
    // to avoid stealing focus from the textarea on every keystroke.

    // Trigger ACO recalculation. WCPA listens for both the jQuery-style
    // change trigger AND the custom events — dispatch the custom events
    // unconditionally so it still works when jQuery isn't on the page.
    setTimeout(() => {
      window.scrollTo(scrollX, scrollY);
      if (window.jQuery) {
        const $ = window.jQuery;
        $('.wcpa_form_outer, .wcpa-form-fields').trigger('change');
      }
      // Native events — work without jQuery
      document.dispatchEvent(new CustomEvent('wcpa:field:change', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('wcpa:update', { bubbles: true }));
      // Some WCPA versions listen on the form itself for an input event
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        form.dispatchEvent(new Event('input', { bubbles: true }));
        form.dispatchEvent(new Event('change', { bubbles: true }));
      }
      window.scrollTo(scrollX, scrollY);
    }, 200);

    // Dispatch custom event for any external listeners
    window.dispatchEvent(new CustomEvent('wsc:configUpdate', {
      detail: { mesh, meshColor, frameSize, color, width, widthFraction, height, heightFraction, hardware, crossbar, comments },
      bubbles: true,
    }));
  }, [acoReady, mesh, meshColor, frameSize, color, width, widthFraction, height, heightFraction, hardware, crossbar, isKit, splineTool]);

  // Debounced comments sync — runs 400ms after the user stops typing,
  // so it doesn't steal focus from the textarea on every keystroke.
  useEffect(() => {
    if (!acoReady || !comments) return;
    const timer = setTimeout(() => {
      const commentsTa = document.querySelector('textarea[id*="field"]');
      if (commentsTa) {
        commentsTa.value = comments;
        commentsTa.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [acoReady, comments]);

  // Computed Values
  const totalWidth = width + widthFraction;
  const totalHeight = height + heightFraction;

  // Scroll to comments function
  const scrollToComments = () => {
    if (commentsRef.current) {
      commentsRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      commentsRef.current.focus();
      setHighlightComments(true);
      setTimeout(() => setHighlightComments(false), 2000); // Remove highlight after 2s
    }
  };

  // Price Calculation
  // Watch the Shopify/ACO price element for live updates via MutationObserver
  const [shopifyBasePrice, setShopifyBasePrice] = useState(null);
  const [acoLivePrice, setAcoLivePrice] = useState(null);
  useEffect(() => {
    if (!acoReady) return;
    // Find the Shopify product price element
    const priceEl =
      document.querySelector('#ProductPrice-product-template .money') ||
      document.querySelector('[data-product-price]') ||
      document.querySelector('.price-item--regular .money') ||
      document.querySelector('.price__regular .money') ||
      document.querySelector('.product__price .money');

    const readPrice = (el) => {
      if (!el) return null;
      const raw = el.textContent.replace(/[^0-9.]/g, '');
      const parsed = parseFloat(raw);
      return (!isNaN(parsed) && parsed > 0) ? parsed : null;
    };

    if (priceEl) {
      // Read initial price
      const initial = readPrice(priceEl);
      if (initial) {
        setShopifyBasePrice(initial);
        setAcoLivePrice(initial);
      }

      // Watch for ACO recalculations that update the price element
      const observer = new MutationObserver(() => {
        const updated = readPrice(priceEl);
        if (updated) setAcoLivePrice(updated);
      });
      observer.observe(priceEl, { childList: true, subtree: true, characterData: true });
      return () => observer.disconnect();
    }
  }, [acoReady]);

  const totalPrice = useMemo(() => {
    // When embedded in Shopify, use the real product variant price as the base
    // and add only the option surcharges. When standalone, use our formula.
    let total;
    if (shopifyBasePrice !== null) {
      // Shopify mode: variant price + ACO option surcharges
      total = shopifyBasePrice;
    } else {
      // Standalone mode: base + united-inch factor (kit uses its own values)
      const base = isKit ? PRICING_KIT.base : PRICING.base;
      const perInch = isKit ? PRICING_KIT.perUnitedInch : PRICING.perUnitedInch;
      total = base;
      const unitedInches = totalWidth + totalHeight;
      total += unitedInches * perInch;
    }
    total += PRICING.mesh[mesh] || 0;
    total += PRICING.hardware[hardware] || 0;
    if (crossbar !== 'none') total += PRICING.crossbar;
    // Kit-only: spline roller tool add-on
    if (isKit && splineTool) total += PRICING.splineTool;
    return total;
  }, [mesh, width, widthFraction, height, heightFraction, hardware, crossbar, shopifyBasePrice, isKit, splineTool]);

  // Bulk pricing — derives active tier, savings, and free-shipping status from qty.
  // KITS DON'T QUALIFY for bulk discount tiers OR free shipping (they always
  // ship at the $12 flat-pack rate). Bulk pricing only applies to the
  // assembled window screen.
  const unitPrice = acoLivePrice || totalPrice;
  const bulkPricing = useMemo(() => {
    const subtotal = unitPrice * quantity;
    if (isKit) {
      // No tiers, no free shipping — flat structure
      return {
        subtotal,
        activeTier: null,
        discountPercent: 0,
        discountAmount: 0,
        freeShipping: false,
        nextMilestone: null,
        remainingToNext: 0,
        progressPct: 0,
      };
    }
    const activeTier = [...BULK_TIERS].reverse().find(t => quantity >= t.minQty) || null;
    const discountPercent = activeTier ? activeTier.percent : 0;
    const discountAmount = subtotal * discountPercent;
    const freeShipping = quantity >= FREE_SHIPPING_MIN_QTY;
    const milestones = [
      ...BULK_TIERS.map(t => ({ minQty: t.minQty, label: t.fullLabel, type: 'discount' })),
      { minQty: FREE_SHIPPING_MIN_QTY, label: 'FREE shipping', type: 'shipping' },
    ];
    const nextMilestone = milestones.find(m => quantity < m.minQty) || null;
    const remainingToNext = nextMilestone ? nextMilestone.minQty - quantity : 0;
    const progressPct = Math.min(100, (quantity / FREE_SHIPPING_MIN_QTY) * 100);
    return { subtotal, activeTier, discountPercent, discountAmount, freeShipping, nextMilestone, remainingToNext, progressPct };
  }, [unitPrice, quantity, isKit]);

  // Dynamic Placeholder/Helper Text based on Hardware
  const getCommentsHelper = () => {
    if (hardware.includes('plunger') || hardware.includes('butterfly')) {
      return "Measurement from corner to the center of the Plunger Bolts or Butterfly Latches are required. Please make note in the Comments. Example: Plunger bolts are 4 inches from corner.";
    }
    if (hardware === 'none') {
      return "Additional hardware specifications such as location. Example: Plunger bolts are 4 inches from corner.";
    }
    return "Additional hardware specifications such as location.";
  };

  // --- Visual Preview Component ---
  const ScreenPreview = () => {
    const aspectRatio = totalWidth / totalHeight;
    const svgHeight = 420;
    const svgWidth = svgHeight * aspectRatio;

    // Original default: cap frame at 420px max width (the natural SVG height).
    // On mobile, also cap based on viewport minus padding.
    const isMobileViewport = viewportWidth < 1024;
    // Kit mode hides the dimension labels (parts are exploded so the labels
    // are misleading), and reserves just enough room around the SVG for the
    // exploded rails (~28px on each side).
    const dimOffset = -15;
    const labelOverhead = isKit ? 40 : 30; // horizontal room outside the SVG
    const verticalLabelExtra = isKit ? 60 : 20; // top + bottom rail clearance
    const desktopMax = 420;
    const mobileMax = Math.max(viewportWidth - 80 - labelOverhead, 120);
    const maxFrameWidth = isMobileViewport ? Math.min(desktopMax, mobileMax) : desktopMax;
    const displayWidth = Math.min(Math.max(svgWidth, 120), maxFrameWidth);
    const displayHeight = displayWidth / aspectRatio;

    // Scale-down for overflow: if the SVG (frame + label overflow) is wider than
    // the container, apply a CSS transform to shrink it. Starts at 1 (no flash).
    // Uses ResizeObserver on the preview wrapper to react to layout changes.
    const [containerScale, setContainerScale] = useState(1);
    useEffect(() => {
      const el = previewWrapRef.current;
      if (!el) return;
      const measure = () => {
        // Available content width = element width minus its horizontal padding
        const cs = getComputedStyle(el);
        const padL = parseFloat(cs.paddingLeft) || 0;
        const padR = parseFloat(cs.paddingRight) || 0;
        const availableWidth = el.clientWidth - padL - padR;
        // Total visual width of the SVG including dimension labels (~25px left + 5px right)
        const totalSvgWidth = displayWidth + labelOverhead;
        const s = availableWidth > 0 ? Math.min(1, availableWidth / totalSvgWidth) : 1;
        setContainerScale(prev => Math.abs(s - prev) > 0.01 ? s : prev);
      };
      measure();
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }, [displayWidth, labelOverhead]);

    const visualFrameThickness = 12; 
    const meshStyle = MESH_STYLES[mesh];
    const frameColor = COLORS[color];
    const hwOption = HARDWARE_OPTIONS.find(h => h.value === hardware);
    
    // Corner View Dimensions
    const selectedFrame = FRAME_SIZES.find(fs => fs.value === frameSize);
    const thicknessValue = selectedFrame?.decimal || 0.3125;
    const faceValue = 0.75;
    
    // Scale for corner view (pixels per inch)
    const scale = 220; 
    const tPx = thicknessValue * scale;
    const fPx = faceValue * scale;

    return (
      <div className="w-full h-full flex flex-col bg-slate-100 rounded-xl border border-slate-200 shadow-inner min-h-[500px]">
        
        {/* View Toggle Header inside Preview */}
        <div className="flex justify-center pt-4 pb-2 gap-2">
           <button 
             onClick={() => setViewMode('full')}
             className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors border ${viewMode === 'full' ? 'bg-white text-[#d44a26] border-[#d44a26]/20 shadow-sm' : 'text-slate-500 border-transparent hover:bg-white/50'}`}
           >
             <Maximize size={14} />
             Full Screen
           </button>
           <button 
             onClick={() => setViewMode('corner')}
             className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1.5 transition-colors border ${viewMode === 'corner' ? 'bg-white text-[#d44a26] border-[#d44a26]/20 shadow-sm' : 'text-slate-500 border-transparent hover:bg-white/50'}`}
           >
             <Box size={14} />
             Corner Profile
           </button>
        </div>

        <div ref={previewWrapRef} className="flex-1 flex flex-col items-center justify-center pt-8 pb-8 pr-4 sm:pr-8 pl-8 sm:pl-12 relative overflow-hidden">

          <div className="relative drop-shadow-xl transition-all duration-500 ease-in-out">

            {viewMode === 'full' ? (
              // --- FULL SCREEN VIEW ---
              // Outer wrapper sets the *layout* size to the scaled dimensions so the
              // parent flex container centers correctly. Inner wrapper uses CSS transform
              // to scale the SVG + hardware overlay down when they'd overflow.
              <div style={{
                width: (displayWidth + labelOverhead) * containerScale,
                height: (displayHeight + verticalLabelExtra) * containerScale,
                overflow: 'visible',
                // Push the inner SVG down so kit-mode labels above y=0 stay inside the box
                paddingTop: isKit ? 32 : 0,
              }}>
              <div style={{
                position: 'relative',
                width: displayWidth,
                height: displayHeight,
                transform: containerScale < 1 ? `scale(${containerScale})` : undefined,
                transformOrigin: 'top left',
              }}>
              <svg
                width={displayWidth}
                height={displayHeight}
                viewBox={`0 0 ${displayWidth} ${displayHeight}`}
                className="overflow-visible"
                style={{ overflow: 'visible' }}
              >
                {/* Defs */}
                <defs>
                  <pattern id="meshGrid" width="4" height="4" patternUnits="userSpaceOnUse">
                    <path d="M 4 0 L 0 0 0 4" fill="none" stroke={meshStyle.color} strokeWidth="0.5" opacity={0.5}/>
                  </pattern>
                  <pattern id="meshDense" width="2" height="2" patternUnits="userSpaceOnUse">
                    <path d="M 2 0 L 0 0 0 2" fill="none" stroke={meshStyle.color} strokeWidth="0.8" opacity={0.8}/>
                  </pattern>
                  <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="#dc2626" />
                  </marker>
                </defs>

                {/* Dimensions — hidden in kit mode since the exploded parts
                    make the labels visually confusing. */}
                {!isKit && (
                  <>
                    <g transform={`translate(0, ${dimOffset})`}>
                      <text x={displayWidth/2} y="0" textAnchor="middle" fill="#64748b" fontSize="11" fontWeight="bold">
                        {totalWidth.toFixed(4).replace(/\.?0+$/, '')}"
                      </text>
                      <line x1="0" y1="5" x2={displayWidth} y2="5" stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 2" />
                    </g>
                    <g transform={`translate(${dimOffset}, 0)`}>
                      <text x="-5" y={displayHeight/2} textAnchor="middle" transform={`rotate(-90, -5, ${displayHeight/2})`} fill="#64748b" fontSize="11" fontWeight="bold">
                        {totalHeight.toFixed(4).replace(/\.?0+$/, '')}"
                      </text>
                      <line x1="5" y1="0" x2="5" y2={displayHeight} stroke="#94a3b8" strokeWidth="1" strokeDasharray="4 2" />
                    </g>
                  </>
                )}

                {/* Frame — assembled (single path with cutout) or exploded (4 separated rails + 4 corner keys) */}
                {!isKit ? (
                  <path
                      d={`M 0 0 H ${displayWidth} V ${displayHeight} H 0 Z M ${visualFrameThickness} ${visualFrameThickness} V ${displayHeight - visualFrameThickness} H ${displayWidth - visualFrameThickness} V ${visualFrameThickness} Z`}
                      fill={frameColor.hex}
                      stroke={frameColor.border}
                      strokeWidth="1"
                      fillRule="evenodd"
                  />
                ) : (() => {
                  // Exploded view: rails pushed outward, corner keys floating
                  // between them, mesh sheet floating in the centre.
                  const E = 28;             // explosion offset for rails
                  const M = 18;             // mesh inset offset
                  const cornerLen = visualFrameThickness * 2.2;
                  return (
                    <g>
                      {/* Subtle assembly guides — dashed lines showing how parts fit */}
                      <g stroke="#cbd5e1" strokeWidth="1" strokeDasharray="3 3" fill="none" opacity="0.7">
                        <line x1={visualFrameThickness / 2}              y1={-E + visualFrameThickness}                                x2={visualFrameThickness / 2}              y2={visualFrameThickness} />
                        <line x1={displayWidth - visualFrameThickness / 2} y1={-E + visualFrameThickness}                                x2={displayWidth - visualFrameThickness / 2} y2={visualFrameThickness} />
                        <line x1={visualFrameThickness / 2}              y1={displayHeight + E - visualFrameThickness}                  x2={visualFrameThickness / 2}              y2={displayHeight - visualFrameThickness} />
                        <line x1={displayWidth - visualFrameThickness / 2} y1={displayHeight + E - visualFrameThickness}                  x2={displayWidth - visualFrameThickness / 2} y2={displayHeight - visualFrameThickness} />
                        <line x1={-E + visualFrameThickness}              y1={visualFrameThickness / 2}                                  x2={visualFrameThickness}                  y2={visualFrameThickness / 2} />
                        <line x1={-E + visualFrameThickness}              y1={displayHeight - visualFrameThickness / 2}                  x2={visualFrameThickness}                  y2={displayHeight - visualFrameThickness / 2} />
                        <line x1={displayWidth + E - visualFrameThickness} y1={visualFrameThickness / 2}                                 x2={displayWidth - visualFrameThickness}    y2={visualFrameThickness / 2} />
                        <line x1={displayWidth + E - visualFrameThickness} y1={displayHeight - visualFrameThickness / 2}                 x2={displayWidth - visualFrameThickness}    y2={displayHeight - visualFrameThickness / 2} />
                      </g>

                      {/* Top rail */}
                      <rect x={visualFrameThickness} y={-E} width={displayWidth - (visualFrameThickness * 2)} height={visualFrameThickness} fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1" rx="1" />
                      {/* Bottom rail */}
                      <rect x={visualFrameThickness} y={displayHeight + E - visualFrameThickness} width={displayWidth - (visualFrameThickness * 2)} height={visualFrameThickness} fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1" rx="1" />
                      {/* Left rail */}
                      <rect x={-E} y={visualFrameThickness} width={visualFrameThickness} height={displayHeight - (visualFrameThickness * 2)} fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1" rx="1" />
                      {/* Right rail */}
                      <rect x={displayWidth + E - visualFrameThickness} y={visualFrameThickness} width={visualFrameThickness} height={displayHeight - (visualFrameThickness * 2)} fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1" rx="1" />

                      {/* 4 corner keys — each L-shape oriented so its elbow
                          faces toward the centre of the frame (real-world
                          assembly direction). */}
                      {(() => {
                        const L = cornerLen;
                        const T = visualFrameThickness;
                        const W = displayWidth;
                        const H = displayHeight;
                        // Each corner has a "bounding box" of L × L. The L-shape
                        // has its horizontal arm along the outside-top/bottom
                        // edge and vertical arm along the outside-left/right
                        // edge, with the inside-elbow pointing at the centre.
                        const paths = [
                          // Top-left: arms go right (along top) and down (along left).
                          `M 0 0 h ${L} v ${T} h -${L - T} v ${L - T} h -${T} Z`,
                          // Top-right: arms go left (along top) and down (along right).
                          `M ${W} 0 h -${L} v ${T} h ${L - T} v ${L - T} h ${T} Z`,
                          // Bottom-left: arms go right (along bottom) and up (along left).
                          `M 0 ${H} h ${L} v -${T} h -${L - T} v -${L - T} h -${T} Z`,
                          // Bottom-right: arms go left (along bottom) and up (along right).
                          `M ${W} ${H} h -${L} v -${T} h ${L - T} v -${L - T} h ${T} Z`,
                        ];
                        return paths.map((d, i) => (
                          <path
                            key={i}
                            d={d}
                            fill={frameColor.hex}
                            stroke={frameColor.border}
                            strokeWidth="1"
                            opacity="0.92"
                          />
                        ));
                      })()}
                    </g>
                  );
                })()}

                {/* Interior Background — only in assembled view */}
                {!isKit && (
                  <rect
                    x={visualFrameThickness}
                    y={visualFrameThickness}
                    width={displayWidth - (visualFrameThickness * 2)}
                    height={displayHeight - (visualFrameThickness * 2)}
                    fill="#e2e8f0"
                  />
                )}

                {/* Mesh sheet — floats centred in kit view, fills opening in assembled */}
                {(() => {
                  const M = isKit ? 18 : 0;
                  const mx = visualFrameThickness + M;
                  const my = visualFrameThickness + M;
                  const mw = displayWidth - (visualFrameThickness * 2) - (M * 2);
                  const mh = displayHeight - (visualFrameThickness * 2) - (M * 2);
                  return (
                    <g>
                      {isKit && (
                        <rect
                          x={mx} y={my} width={mw} height={mh}
                          fill="#f1f5f9"
                          stroke="#cbd5e1"
                          strokeDasharray="4 3"
                          strokeWidth="1"
                          rx="2"
                        />
                      )}
                      <rect
                        x={mx} y={my} width={mw} height={mh}
                        fill={meshStyle.pattern === 'dense' || meshStyle.pattern === 'thick' ? 'url(#meshDense)' : 'url(#meshGrid)'}
                        opacity={meshStyle.opacity + 0.2}
                      />
                      <rect
                        x={mx} y={my} width={mw} height={mh}
                        fill="black"
                        opacity={meshStyle.opacity}
                      />
                    </g>
                  );
                })()}

                {/* Crossbar */}
                {crossbar === 'width' && (
                  <rect
                    x={visualFrameThickness}
                    y={(displayHeight / 2) - (visualFrameThickness/2)}
                    width={displayWidth - (visualFrameThickness * 2)}
                    height={visualFrameThickness}
                    fill={frameColor.hex}
                    stroke={frameColor.border}
                    strokeWidth="1"
                  />
                )}
                {crossbar === 'height' && (
                  <rect
                    x={(displayWidth / 2) - (visualFrameThickness/2)}
                    y={visualFrameThickness}
                    width={visualFrameThickness}
                    height={displayHeight - (visualFrameThickness * 2)}
                    fill={frameColor.hex}
                    stroke={frameColor.border}
                    strokeWidth="1"
                  />
                )}

                {/* Red measurement arrows for plunger/butterfly hardware —
                    One arrow per hardware piece, from the nearest frame corner/edge
                    to the center of that piece. Arrow points toward the piece. */}
                {hwOption && (hwOption.type === 'plunger' || hwOption.type === 'butterfly') && (() => {
                  const vft = visualFrameThickness;
                  const frameIntW = displayWidth - vft * 2;
                  const frameIntH = displayHeight - vft * 2;
                  const pieces = HARDWARE_PIECES[hwOption.value] || [];

                  return (
                    <g className="measurement-arrows">
                      <defs>
                        <marker id="measArrowEnd" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto" markerUnits="userSpaceOnUse">
                          <polygon points="0 0, 8 3, 0 6" fill="#dc2626" />
                        </marker>
                        <marker id="measDot" markerWidth="6" markerHeight="6" refX="3" refY="3" markerUnits="userSpaceOnUse">
                          <circle cx="3" cy="3" r="3" fill="#dc2626" />
                        </marker>
                      </defs>
                      {pieces.map((piece, i) => {
                        // Center of this piece along its edge
                        const pieceCenterAlongEdge = piece.pos; // 0-1 fraction

                        // Determine the piece center in SVG coordinates and the
                        // nearest frame corner to draw from.
                        let pieceX, pieceY, cornerX, cornerY;

                        if (piece.edge === 'left') {
                          pieceX = vft / 2; // center of frame bar
                          pieceY = vft + pieceCenterAlongEdge * frameIntH;
                          // Nearest corner: top-left or bottom-left
                          cornerX = pieceX;
                          cornerY = pieceCenterAlongEdge <= 0.5 ? 0 : displayHeight;
                        } else if (piece.edge === 'right') {
                          pieceX = displayWidth - vft / 2;
                          pieceY = vft + pieceCenterAlongEdge * frameIntH;
                          cornerX = pieceX;
                          cornerY = pieceCenterAlongEdge <= 0.5 ? 0 : displayHeight;
                        } else if (piece.edge === 'top') {
                          pieceX = vft + pieceCenterAlongEdge * frameIntW;
                          pieceY = vft / 2;
                          cornerX = pieceCenterAlongEdge <= 0.5 ? 0 : displayWidth;
                          cornerY = pieceY;
                        } else {
                          // bottom
                          pieceX = vft + pieceCenterAlongEdge * frameIntW;
                          pieceY = displayHeight - vft / 2;
                          cornerX = pieceCenterAlongEdge <= 0.5 ? 0 : displayWidth;
                          cornerY = pieceY;
                        }

                        return (
                          <g key={`arrow-${i}`}>
                            <line
                              x1={cornerX} y1={cornerY}
                              x2={pieceX} y2={pieceY}
                              stroke="#dc2626"
                              strokeWidth="1.5"
                              strokeDasharray="4 2"
                              markerStart="url(#measDot)"
                              markerEnd="url(#measArrowEnd)"
                            />
                          </g>
                        );
                      })}
                      {/* Center label */}
                      <foreignObject x={vft} y={vft}
                        width={frameIntW} height={frameIntH}>
                        <div xmlns="http://www.w3.org/1999/xhtml" style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: '100%', height: '100%', pointerEvents: 'none',
                        }}>
                          <div style={{
                            color: '#fff', fontSize: '10px', fontWeight: 'bold',
                            textAlign: 'center', lineHeight: '1.35',
                            padding: '8px 12px', backgroundColor: 'rgba(0,0,0,0.5)',
                            borderRadius: '5px', maxWidth: '80%',
                          }}>
                            Measure from corner to center of each bolt/latch.<br/>Note measurements in Comments.
                          </div>
                        </div>
                      </foreignObject>
                    </g>
                  );
                })()}
              </svg>

              {/* Hardware pieces - cropped from CDN overlay images,
                  anchored to the correct frame edge with proportional spacing along that edge.
                  Each piece knows its edge (top/bottom/left/right) and position (0-1) along it. */}
              {hwOption && hwOption.value !== 'none' && HARDWARE_PIECES[hwOption.value] && (() => {
                const vft = visualFrameThickness;
                const frameIntW = displayWidth - vft * 2;  // inner mesh width
                const frameIntH = displayHeight - vft * 2; // inner mesh height
                const CDN_FW = CDN_FRAME.right - CDN_FRAME.left; // 390
                const CDN_FH = CDN_FRAME.bottom - CDN_FRAME.top; // 479

                // Uniform scale: geometric mean preserves piece aspect ratios across any frame shape
                const pieceScale = Math.sqrt((frameIntW * frameIntH) / (CDN_FW * CDN_FH));
                const imgUrl = HARDWARE_IMAGE_MAP[hwOption.value];
                const pieces = HARDWARE_PIECES[hwOption.value];
                const pad = 4;

                return (
                  <div style={{
                    position: 'absolute', top: 0, left: 0,
                    width: displayWidth, height: displayHeight,
                    overflow: 'visible',
                    pointerEvents: 'none', zIndex: 1,
                  }}>
                    {pieces.map((piece, i) => {
                      // Crop box with padding (CDN canvas pixels)
                      const px = Math.max(0, piece.x - pad);
                      const py = Math.max(0, piece.y - pad);
                      const pw = Math.min(CDN_CANVAS.w - px, piece.w + pad * 2);
                      const ph = Math.min(CDN_CANVAS.h - py, piece.h + pad * 2);

                      // Scaled piece size (ensure minimum visibility)
                      const sw = Math.max(pw * pieceScale, 8);
                      const sh = Math.max(ph * pieceScale, 8);

                      // Edge-anchored positioning:
                      // Positive inset = toward mesh center; negative = toward frame exterior.
                      // Per-piece override, then per-type default, then fallback to 4px inward.
                      const INSET_BY_TYPE = {
                        springs_h: -6, springs_w: -6,   // springs sit ON the frame
                        ram_h: -4, ram_w: -4,            // ram horn clips sit on frame edge
                        swivel_h: -6, swivel_w: -6,     // acorn/swivel latches sit on frame
                        // Plunger bolts straddle the frame edge (gap sits on frame)
                        plunger2_same_h: 0, plunger2_same_w: 0,
                        plunger4_each_h: 0, plunger4_each_w: 0,
                        plunger2_each_h: 0, plunger2_each_w: 0,
                        // Butterfly latches sit ON the frame (like springs/swivels)
                        butterfly2_each_h: -6, butterfly2_each_w: -6,
                        butterfly2_same_h: -6, butterfly2_same_w: -6,
                        butterfly4_each_h: -6, butterfly4_each_w: -6,
                        // Wood hardware: hangers mount on top of frame, latches on bottom
                        wood: -4,
                      };
                      const inset = piece.inset ?? INSET_BY_TYPE[hwOption.value] ?? 4;
                      let pieceLeft, pieceTop;

                      if (piece.edge === 'top') {
                        pieceLeft = vft + piece.pos * frameIntW - sw / 2;
                        pieceTop = vft - sh / 2 + inset;
                      } else if (piece.edge === 'bottom') {
                        pieceLeft = vft + piece.pos * frameIntW - sw / 2;
                        pieceTop = vft + frameIntH - sh / 2 - inset;
                      } else if (piece.edge === 'left') {
                        pieceLeft = vft - sw / 2 + inset;
                        pieceTop = vft + piece.pos * frameIntH - sh / 2;
                      } else {
                        // right edge
                        pieceLeft = vft + frameIntW - sw / 2 - inset;
                        pieceTop = vft + piece.pos * frameIntH - sh / 2;
                      }

                      return (
                        <div key={i} style={{
                          position: 'absolute',
                          left: pieceLeft,
                          top: pieceTop,
                          width: sw,
                          height: sh,
                          overflow: 'hidden',
                          pointerEvents: 'none',
                        }}>
                          <img
                            src={imgUrl}
                            style={{
                              position: 'absolute',
                              left: -(px * pieceScale),
                              top: -(py * pieceScale),
                              width: CDN_CANVAS.w * pieceScale,
                              height: CDN_CANVAS.h * pieceScale,
                              maxWidth: 'none',
                              maxHeight: 'none',
                              pointerEvents: 'none',
                            }}
                            crossOrigin="anonymous"
                          />
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              </div>
              </div>
            ) : (
              // --- 3D ROTATED SCREEN PANEL VIEW (matches live site style) ---
              <div className="flex flex-col items-center">
                {(() => {
                  // Large 3D perspective view of the screen panel rotated to show frame profile.
                  // The 3/4" measures the HEIGHT of the bottom frame bar (vertical on front face).
                  // The 5/16" measures the DEPTH front-to-back (z-axis).

                  const panelW = 340;   // screen width (larger for bigger render)
                  const panelH = 460;   // screen height
                  const frameBar = 24;  // frame bar width on the panel face (represents 3/4")
                  const frameDepth = thicknessValue * 55; // frame depth (z-axis)

                  const rotY = 56 * Math.PI / 180;
                  const rotX = 4 * Math.PI / 180;
                  const focal = 800;

                  const cosY = Math.cos(rotY), sinY = Math.sin(rotY);
                  const cosX = Math.cos(rotX), sinX = Math.sin(rotX);

                  const project = (x3d, y3d, z3d) => {
                    const cx = x3d - panelW * 0.42;
                    const cy = y3d - panelH * 0.46;
                    const cz = z3d + frameDepth * 0.5;
                    const rx = cx * cosY - cz * sinY;
                    const rz = cx * sinY + cz * cosY;
                    const ry = cy * cosX - rz * sinX;
                    const rz2 = cy * sinX + rz * cosX;
                    const scale = focal / (focal + rz2 + 200);
                    return [240 + rx * scale, 175 + ry * scale];
                  };

                  const pt = (x, y, z) => project(x, y, z);
                  const poly = (pts) => pts.map(p => pt(...p).join(',')).join(' ');

                  const meshFill = mesh === 'solar' ? '#1a1a1a' : mesh === 'pet' ? '#111' : mesh === 'no-see-um' ? '#222' : '#555';
                  const meshOpacity = mesh === 'standard' ? 0.7 : 0.8;

                  const fb = frameBar;
                  const W = panelW, H = panelH, D = frameDepth;


                  return (
                    <svg width={isMobileViewport ? Math.min(480, viewportWidth - 100) : 480} height={isMobileViewport ? Math.min(440, (viewportWidth - 100) * (440/480)) : 440} viewBox="-40 -50 520 490" className="overflow-visible">
                      <defs>
                        <filter id="panelShadow" x="-15%" y="-10%" width="135%" height="130%">
                          <feDropShadow dx="3" dy="5" stdDeviation="6" floodOpacity="0.12"/>
                        </filter>
                        <pattern id="meshGrid3d" width="5" height="5" patternUnits="userSpaceOnUse">
                          <rect width="5" height="5" fill="transparent"/>
                          <line x1="0" y1="0" x2="5" y2="0" stroke={meshFill} strokeWidth="0.6" opacity="0.7"/>
                          <line x1="0" y1="0" x2="0" y2="5" stroke={meshFill} strokeWidth="0.6" opacity="0.7"/>
                        </pattern>
                      </defs>

                      <g filter="url(#panelShadow)">

                        {/* --- BACK/SIDE FACES (depth strips) --- */}
                        {/* Left edge depth strip */}
                        <polygon
                          points={poly([
                            [0, 0, -D], [0, H, -D], [0, H, 0], [0, 0, 0]
                          ])}
                          fill={frameColor.hex}
                          stroke={frameColor.border}
                          strokeWidth="0.8"
                          opacity="0.55"
                        />
                        {/* Bottom edge depth strip */}
                        <polygon
                          points={poly([
                            [0, H, -D], [W, H, -D], [W, H, 0], [0, H, 0]
                          ])}
                          fill={frameColor.hex}
                          stroke={frameColor.border}
                          strokeWidth="0.8"
                          opacity="0.45"
                        />
                        {/* Top edge depth strip */}
                        <polygon
                          points={poly([
                            [0, 0, 0], [W, 0, 0], [W, 0, -D], [0, 0, -D]
                          ])}
                          fill={frameColor.hex}
                          stroke={frameColor.border}
                          strokeWidth="0.8"
                          opacity="0.5"
                        />

                        {/* --- MESH (center of front face at z=0) --- */}
                        <polygon
                          points={poly([
                            [fb, fb, 0], [W - fb, fb, 0],
                            [W - fb, H - fb, 0], [fb, H - fb, 0]
                          ])}
                          fill={meshFill}
                          opacity={meshOpacity}
                        />
                        <polygon
                          points={poly([
                            [fb, fb, 0], [W - fb, fb, 0],
                            [W - fb, H - fb, 0], [fb, H - fb, 0]
                          ])}
                          fill="url(#meshGrid3d)"
                          opacity="0.8"
                        />

                        {/* --- FRONT FACE FRAME BARS (z=0) --- */}
                        {/* Top bar */}
                        <polygon
                          points={poly([[0, 0, 0], [W, 0, 0], [W, fb, 0], [0, fb, 0]])}
                          fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1"
                        />
                        {/* Bottom bar */}
                        <polygon
                          points={poly([[0, H - fb, 0], [W, H - fb, 0], [W, H, 0], [0, H, 0]])}
                          fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1"
                        />
                        {/* Left bar */}
                        <polygon
                          points={poly([[0, 0, 0], [fb, 0, 0], [fb, H, 0], [0, H, 0]])}
                          fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1"
                        />
                        {/* Right bar */}
                        <polygon
                          points={poly([[W - fb, 0, 0], [W, 0, 0], [W, H, 0], [W - fb, H, 0]])}
                          fill={frameColor.hex} stroke={frameColor.border} strokeWidth="1"
                        />

                        {/* Spline channel grooves */}
                        <line
                          x1={pt(fb * 0.55, fb * 1.5, 0)[0]} y1={pt(fb * 0.55, fb * 1.5, 0)[1]}
                          x2={pt(fb * 0.55, H - fb * 1.5, 0)[0]} y2={pt(fb * 0.55, H - fb * 1.5, 0)[1]}
                          stroke={frameColor.border} strokeWidth="0.5" opacity="0.35"
                        />
                        <line
                          x1={pt(fb * 1.5, fb * 0.55, 0)[0]} y1={pt(fb * 1.5, fb * 0.55, 0)[1]}
                          x2={pt(W - fb * 1.5, fb * 0.55, 0)[0]} y2={pt(W - fb * 1.5, fb * 0.55, 0)[1]}
                          stroke={frameColor.border} strokeWidth="0.5" opacity="0.35"
                        />

                        {/* Corner mitre lines */}
                        <line x1={pt(0,0,0)[0]} y1={pt(0,0,0)[1]} x2={pt(fb,fb,0)[0]} y2={pt(fb,fb,0)[1]} stroke={frameColor.border} strokeWidth="0.8" opacity="0.5" />
                        <line x1={pt(W,0,0)[0]} y1={pt(W,0,0)[1]} x2={pt(W-fb,fb,0)[0]} y2={pt(W-fb,fb,0)[1]} stroke={frameColor.border} strokeWidth="0.8" opacity="0.5" />
                        <line x1={pt(0,H,0)[0]} y1={pt(0,H,0)[1]} x2={pt(fb,H-fb,0)[0]} y2={pt(fb,H-fb,0)[1]} stroke={frameColor.border} strokeWidth="0.8" opacity="0.5" />
                        <line x1={pt(W,H,0)[0]} y1={pt(W,H,0)[1]} x2={pt(W-fb,H-fb,0)[0]} y2={pt(W-fb,H-fb,0)[1]} stroke={frameColor.border} strokeWidth="0.8" opacity="0.5" />
                      </g>

                      {/* --- DIMENSION LABELS --- */}

                      {/* 3/4" = FACE HEIGHT: the HEIGHT of the bottom frame bar.
                          Vertical bracket to the LEFT of the bottom-left corner,
                          measuring from the inner top edge of the bottom bar (y=H-fb)
                          down to the outer bottom edge (y=H). */}
                      {(() => {
                        const xOff = -12; // offset left of the panel edge
                        const pTop = pt(xOff, H - fb, 0);    // inner edge of bottom bar
                        const pBot = pt(xOff, H, 0);          // outer edge of bottom bar
                        const mid = [(pTop[0] + pBot[0]) / 2, (pTop[1] + pBot[1]) / 2];
                        return (
                          <g>
                            {/* Vertical bracket line */}
                            <line x1={pTop[0]} y1={pTop[1]} x2={pBot[0]} y2={pBot[1]} stroke="#475569" strokeWidth="1.2" />
                            {/* Horizontal tick marks */}
                            <line x1={pTop[0] - 5} y1={pTop[1]} x2={pTop[0] + 5} y2={pTop[1]} stroke="#475569" strokeWidth="1.2" />
                            <line x1={pBot[0] - 5} y1={pBot[1]} x2={pBot[0] + 5} y2={pBot[1]} stroke="#475569" strokeWidth="1.2" />
                            {/* Label to the left */}
                            <text x={mid[0] - 10} y={mid[1] + 5} fill="#475569" fontSize="14" fontWeight="bold" textAnchor="end">
                              3/4"
                            </text>
                          </g>
                        );
                      })()}

                      {/* 5/16" = DEPTH: how far the frame extends front-to-back.
                          Horizontal bracket below the bottom-left corner,
                          measuring from front face (z=0) to back face (z=-D). */}
                      {(() => {
                        const yOff = H + 12;
                        const pFront = pt(0, yOff, 0);
                        const pBack = pt(0, yOff, -D);
                        const mid = [(pFront[0] + pBack[0]) / 2, (pFront[1] + pBack[1]) / 2];
                        return (
                          <g>
                            {/* Horizontal bracket line */}
                            <line x1={pFront[0]} y1={pFront[1]} x2={pBack[0]} y2={pBack[1]} stroke="#475569" strokeWidth="1.2" />
                            {/* Vertical tick marks */}
                            <line x1={pFront[0]} y1={pFront[1] - 5} x2={pFront[0]} y2={pFront[1] + 5} stroke="#475569" strokeWidth="1.2" />
                            <line x1={pBack[0]} y1={pBack[1] - 5} x2={pBack[0]} y2={pBack[1] + 5} stroke="#475569" strokeWidth="1.2" />
                            {/* Label below */}
                            <text x={mid[0]} y={mid[1] + 16} fill="#475569" fontSize="13" fontWeight="bold" textAnchor="middle">
                              {frameSize}"
                            </text>
                          </g>
                        );
                      })()}
                    </svg>
                  );
                })()}
                <div className="mt-1 text-center">
                  <p className="text-xs text-slate-500 max-w-xs mx-auto">
                    {frameSize}" thickness × 3/4" face — {COLORS[color].name} frame with {MESH_STYLES[mesh].name.toLowerCase()}
                  </p>
                </div>
              </div>
            )}

          </div>
          
          {viewMode === 'full' && (
            <div className="mt-8 flex flex-col items-center gap-2">
              <p className="text-slate-500 text-sm font-medium">Visual Representation</p>

              {/* Mesh Thumbnail Gallery */}
              {MESH_IMAGES[mesh] && MESH_IMAGES[mesh].length > 0 && (
                <div className="flex flex-col items-center gap-2 mt-2">
                  <p className="text-slate-400 text-xs">Click to view real {MESH_STYLES[mesh].name}</p>
                  <div className="flex gap-2 justify-center flex-wrap">
                    {MESH_IMAGES[mesh].map((imgUrl, idx) => (
                      <button
                        key={idx}
                        onClick={() => setLightboxImg(imgUrl)}
                        className="w-14 h-14 rounded-lg overflow-hidden border-2 border-slate-200 hover:border-[#d44a26] transition-all shadow-sm hover:shadow-md cursor-pointer"
                        title={`View ${MESH_STYLES[mesh].name} photo ${idx + 1}`}
                      >
                        <img
                          src={imgUrl}
                          alt={`${MESH_STYLES[mesh].name} photo ${idx + 1}`}
                          className="w-full h-full object-cover"
                          crossOrigin="anonymous"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hardware !== 'none' && (
                <button
                  onClick={scrollToComments}
                  className="flex items-center gap-2 text-xs text-[#d44a26] bg-[#d44a26]/10 px-4 py-2 rounded-full border border-[#d44a26]/20 hover:bg-[#d44a26]/20 transition-colors shadow-sm cursor-pointer"
                >
                  <ZoomIn size={14} />
                  <span>Check hardware position & add comments</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  // --- JS-based scroll-follow for the Live Preview ---
  // CSS position:sticky doesn't work on Shopify because the theme has
  // overflow:hidden on ancestor elements. This effect manually positions
  // the preview card as "fixed" while scrolling, and stops it when it
  // reaches the Order Summary ("Your Build") section.
  useEffect(() => {
    const col = previewColumnRef.current;
    const inner = previewInnerRef.current;
    const summary = orderSummaryRef.current;
    if (!col || !inner || !summary) return;

    // Only apply on desktop (lg breakpoint = 1024px+)
    const mql = window.matchMedia('(min-width: 1024px)');
    let ticking = false;
    const topOffset = isEmbedded ? 16 : 96; // matches the old lg:top-4 / lg:top-24

    const update = () => {
      ticking = false;
      if (!mql.matches) {
        // Mobile: reset everything
        inner.style.position = '';
        inner.style.top = '';
        inner.style.width = '';
        return;
      }

      const colRect = col.getBoundingClientRect();
      const innerH = inner.offsetHeight;
      const summaryRect = summary.getBoundingClientRect();
      // Bottom limit: top of the Order Summary card minus a gap
      const bottomLimit = summaryRect.top - 16;

      if (colRect.top >= topOffset) {
        // Column top is still in view — sit at natural position
        inner.style.position = '';
        inner.style.top = '';
        inner.style.width = '';
      } else if (topOffset + innerH <= bottomLimit) {
        // Fix in viewport — column has scrolled up but we haven't hit the summary yet
        inner.style.position = 'fixed';
        inner.style.top = topOffset + 'px';
        inner.style.width = colRect.width + 'px';
      } else {
        // Pin at the bottom — the summary is close, park it above
        const parentRect = col.getBoundingClientRect();
        const pinnedTop = summaryRect.top - innerH - 16 - parentRect.top;
        inner.style.position = 'relative';
        inner.style.top = Math.max(0, pinnedTop) + 'px';
        inner.style.width = '';
      }
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    };

    const onResize = () => update();

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    update(); // initial position

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      inner.style.position = '';
      inner.style.top = '';
      inner.style.width = '';
    };
  }, [isEmbedded]);

  // --- Render ---

  return (
    <div className={isEmbedded ? 'text-slate-900 font-sans selection:bg-[#d44a26]/20' : 'min-h-screen bg-slate-50 text-slate-900 font-sans selection:bg-[#d44a26]/20'}>

      {/* Embedded: sticky price bar that shows the live ACO price */}
      {isEmbedded && (
        <div className="flex items-center justify-between gap-4 px-5 py-3 mb-6 rounded-xl shadow-lg"
          style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
          <div className="flex items-center gap-3">
            <Ruler size={20} className="text-white/80" />
            <span className="text-white/90 font-medium text-sm">Estimated Total</span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-2xl font-bold text-white tracking-tight leading-none">
              ${(bulkPricing.subtotal - bulkPricing.discountAmount).toFixed(2)}
            </span>
            {quantity > 1 && (
              <span className="text-[11px] text-white/80 mt-0.5">
                {quantity} × ${unitPrice.toFixed(2)}
                {bulkPricing.discountAmount > 0 && ` · save $${bulkPricing.discountAmount.toFixed(2)}`}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Editing-this-cart-line notice */}
      {editLineKey && (
        <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg bg-green-50 border border-green-200 text-sm text-green-900">
          <Check size={16} className="text-green-700 shrink-0" />
          <span>
            Editing your existing cart item. <strong>Save Changes</strong> will replace it with your updated build.
          </span>
        </div>
      )}

      {/* Header — hidden when embedded in Shopify */}
      {!isEmbedded && (
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-[#d44a26] text-white p-2.5 rounded-lg shadow-sm">
                <Ruler size={22} />
              </div>
              <div>
                 <h1 className="text-xl font-bold text-slate-900 leading-none">Custom Window Screen</h1>
                 <p className="text-xs text-slate-500 mt-1 font-medium tracking-wide">REPLACEMENT CONFIGURATOR</p>
              </div>
            </div>
            <div className="flex flex-col items-end">
              <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Estimated Total</span>
              <span className="text-2xl font-bold text-[#d44a26] leading-none">
                ${(bulkPricing.subtotal - bulkPricing.discountAmount).toFixed(2)}
              </span>
              {quantity > 1 && (
                <span className="text-[10px] text-slate-500 mt-0.5">
                  {quantity} × ${unitPrice.toFixed(2)}
                  {bulkPricing.discountAmount > 0 && (
                    <span className="text-green-600 font-semibold"> · save ${bulkPricing.discountAmount.toFixed(2)}</span>
                  )}
                </span>
              )}
            </div>
          </div>
        </header>
      )}

      <main className={isEmbedded ? 'grid grid-cols-1 lg:grid-cols-12 gap-8' : 'max-w-7xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-12 gap-8'}>
        
        {/* Left Column (Desktop): Preview — JS scroll-follow since CSS sticky
            is broken by Shopify theme's overflow:hidden ancestors */}
        <div className="lg:col-span-5" ref={previewColumnRef}>
          <div ref={previewInnerRef}>

            {/* Live Preview */}
            <div ref={previewCardRef} className="bg-white rounded-xl shadow-lg border border-slate-200 ring-1 ring-slate-100">
              <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                <h3 className="font-semibold text-slate-700 flex items-center gap-2">
                  Live Preview
                  {isKit && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 rounded-full">
                      <Box size={11} /> DIY Kit
                    </span>
                  )}
                </h3>
                <span className="text-xs bg-white border border-slate-200 text-slate-600 px-2 py-1 rounded font-medium shadow-sm">
                  {FRAME_SIZES.find(f => f.value === frameSize)?.label}
                </span>
              </div>
              {isKit && (
                <div className="mx-4 mt-4 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
                  <Box size={18} className="text-amber-600 mt-0.5 shrink-0" />
                  <div className="leading-tight">
                    <p className="text-sm font-semibold text-amber-900">
                      Some assembly required
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      This is a DIY kit — ships flat-packed as separate frame rails, corner keys, mesh, and spline. The exploded view below shows the parts included. Assembles in 10–15 minutes with the included spline roller tool.
                    </p>
                  </div>
                </div>
              )}
              <div className="p-6">
                <ScreenPreview />
              </div>
            </div>

          </div>
        </div>

        {/* Right Column (Desktop): Configurator Form */}
        <div className="lg:col-span-7 space-y-8">

           {/* Dimensions */}
           <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#d44a26] text-white text-sm font-bold shadow-sm shadow-orange-200">1</span>
              Dimensions
            </h2>
            <div className="bg-[#fff7ed] border border-[#ffedd5] p-4 rounded-lg mb-6 flex gap-3">
              <Info className="text-[#d44a26] shrink-0 mt-0.5" size={18} />
              <div className="text-sm text-slate-700 leading-relaxed">
                <p className="font-medium text-[#9a3412] mb-1">Critical Measurement Tip:</p>
                Measure your existing screen frame from <strong>outside edge to outside edge</strong>. 
                Do not measure the window opening (daylight opening) unless your screen sits on a flat plane on all 4 sides.
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Width */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Width (Inches)</label>
                <div className="flex gap-2">
                  <select
                    value={width}
                    onChange={(e) => { setWidth(Number(e.target.value)); setViewMode('full'); }}
                    className="flex-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-2.5 border bg-slate-50 focus:bg-white transition-colors"
                  >
                    {Array.from({ length: WIDTH_RANGE.max - WIDTH_RANGE.min + 1 }, (_, i) => WIDTH_RANGE.min + i).map(num => (
                      <option key={num} value={num}>{num}"</option>
                    ))}
                  </select>
                  <select
                    value={widthFraction}
                    onChange={(e) => { setWidthFraction(Number(e.target.value)); setViewMode('full'); }}
                    className="w-24 block rounded-md border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-2.5 border bg-slate-50 focus:bg-white transition-colors"
                  >
                    {FRACTIONS.map(f => (
                      <option key={f.val} value={f.val}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Height */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Height (Inches)</label>
                <div className="flex gap-2">
                  <select
                    value={height}
                    onChange={(e) => { setHeight(Number(e.target.value)); setViewMode('full'); }}
                    className="flex-1 block w-full rounded-md border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-2.5 border bg-slate-50 focus:bg-white transition-colors"
                  >
                    {Array.from({ length: HEIGHT_RANGE.max - HEIGHT_RANGE.min + 1 }, (_, i) => HEIGHT_RANGE.min + i).map(num => (
                      <option key={num} value={num}>{num}"</option>
                    ))}
                  </select>
                  <select
                    value={heightFraction}
                    onChange={(e) => { setHeightFraction(Number(e.target.value)); setViewMode('full'); }}
                    className="w-24 block rounded-md border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-2.5 border bg-slate-50 focus:bg-white transition-colors"
                  >
                    {FRACTIONS.map(f => (
                      <option key={f.val} value={f.val}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Manufacturing tolerance */}
            <p className="text-xs text-slate-500 mt-4 leading-relaxed">
              Each screen is custom-built to your exact measurements — please allow a manufacturing tolerance of <strong className="text-slate-700">±1/16"</strong>.
            </p>
          </section>

          {/* Frame Style & Color */}
          <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#d44a26] text-white text-sm font-bold shadow-sm shadow-orange-200">2</span>
              Frame Specifications
            </h2>
            
            <div className="mb-8">
               <label className="block text-sm font-medium text-slate-700 mb-3">Frame Thickness</label>
               <div className="grid grid-cols-3 gap-3">
                 {FRAME_SIZES.map(fs => (
                    <button
                      key={fs.value}
                      onClick={() => { setFrameSize(fs.value); setViewMode('corner'); }}
                      className={`
                        py-3 px-3 text-sm rounded-lg border-2 transition-all font-medium
                        ${frameSize === fs.value 
                          ? 'border-[#d44a26] bg-[#d44a26]/5 text-[#d44a26]' 
                          : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}
                      `}
                    >
                      {fs.label}
                    </button>
                 ))}
               </div>
               <p className="text-xs text-slate-400 mt-2">Selecting a size shows the profile corner view. Changing any other option returns to the full view.</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">Frame Color</label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {Object.keys(COLORS).map(c => (
                  <button
                    key={c}
                    onClick={() => { setColor(c); setViewMode('full'); }}
                    className={`
                      group flex flex-col items-center gap-2 p-2 rounded-lg border-2 transition-all
                      ${color === c ? 'border-[#d44a26] bg-[#d44a26]/5 ring-1 ring-[#d44a26]/20' : 'border-transparent hover:bg-slate-50'}
                    `}
                  >
                    <div 
                      className="w-10 h-10 rounded-full shadow-sm border border-slate-200" 
                      style={{ backgroundColor: COLORS[c].hex }}
                    />
                    <span className={`text-xs font-medium text-center ${color === c ? 'text-[#d44a26]' : 'text-slate-600'}`}>
                      {COLORS[c].name}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Mesh Selection */}
          <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <h2 className="text-lg font-semibold mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#d44a26] text-white text-sm font-bold shadow-sm shadow-orange-200">3</span>
              Mesh Type
            </h2>
            <div className="grid grid-cols-1 gap-3">
              {Object.keys(MESH_STYLES)
                .filter(mKey => !(isKit && (mKey === 'aluminum' || mKey === 'seevue')))
                .map(mKey => (
                 <RadioCard
                   key={mKey}
                   selected={mesh === mKey}
                   onClick={() => { setMesh(mKey); setViewMode('full'); }}
                   title={MESH_STYLES[mKey].name}
                   price={PRICING.mesh[mKey]}
                   subtitle={
                     mKey === 'standard' ? 'Standard fiberglass mesh.' :
                     mKey === 'bettervue' ? 'Improved visibility and airflow.' :
                     mKey === 'ultravue' ? 'Premium clarity and airflow.' :
                     mKey === 'solar' ? 'Blocks 65-70% of heat and UV.' :
                     mKey === 'aluminum' ? 'Classic aluminum screen mesh.' :
                     mKey === 'petscreen' ? '7x stronger than standard mesh.' :
                     mKey === 'noseeum' ? 'Fine 20x20 weave blocks tiny insects.' :
                     mKey === 'seevue' ? 'Premium stainless steel mesh.' : ''
                   }
                 />
              ))}
            </div>

            {/* Mesh Color - dynamic per mesh type */}
            <div className="mt-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">Mesh Color</label>
              <div className="flex gap-3">
                {(MESH_COLORS[mesh] || ['charcoal']).map((colorKey) => (
                  <button
                    key={colorKey}
                    type="button"
                    onClick={() => { setMeshColor(colorKey); setViewMode('full'); }}
                    className={`flex-1 rounded-lg border-2 p-3 text-center text-sm font-medium transition-all ${
                      meshColor === colorKey
                        ? 'border-[#d44a26] bg-[#d44a26]/5 text-[#d44a26]'
                        : 'border-slate-200 text-slate-700 hover:border-slate-300'
                    }`}
                  >
                    {MESH_COLOR_LABELS[colorKey] || colorKey}
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Hardware & Crossbar */}
          <section className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
             <h2 className="text-lg font-semibold mb-4 flex items-center gap-3">
              <span className="flex items-center justify-center w-7 h-7 rounded-full bg-[#d44a26] text-white text-sm font-bold shadow-sm shadow-orange-200">4</span>
              Hardware & Options
            </h2>
            
            <div className="space-y-8">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Mounting Hardware</label>
                <div className="relative">
                  <select
                    value={hardware}
                    onChange={(e) => { setHardware(e.target.value); setViewMode('full'); }}
                    className="block w-full rounded-lg border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-3 border bg-slate-50"
                  >
                    {HARDWARE_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>
                         {opt.label}{opt.price > 0 ? ` ($${opt.price.toFixed(2)})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mt-3 flex items-start gap-2 text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-100">
                   <HelpCircle size={16} className="shrink-0 mt-0.5" />
                   <p>
                     Hardware is installed based on standard industry locations.
                     See the red indicators on the preview to visualize placement.
                   </p>
                </div>
                {/* Red measurement warning for plunger/butterfly (matches live store) */}
                {(hardware.includes('plunger') || hardware.includes('butterfly')) && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-xs font-semibold">
                    Measurement from corner to the center of the Plunger Bolts or Butterfly Latches are required. Please make note in the Comment Box Below.
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-3">Crossbar (Optional)</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                   <button
                      onClick={() => { setCrossbar('none'); setViewMode('full'); }}
                      className={`
                        py-3 px-3 text-sm rounded-lg border-2 transition-all
                        ${crossbar === 'none'
                          ? 'border-[#d44a26] bg-[#d44a26]/5 text-[#d44a26] font-semibold'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}
                      `}
                    >
                      None
                    </button>
                    <button
                      onClick={() => { setCrossbar('width'); setViewMode('full'); }}
                      className={`
                        py-3 px-3 text-sm rounded-lg border-2 transition-all flex flex-col items-center justify-center
                        ${crossbar === 'width'
                          ? 'border-[#d44a26] bg-[#d44a26]/5 text-[#d44a26] font-semibold'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}
                      `}
                    >
                      <span>Parallel to Width</span>
                      <span className="text-[10px] uppercase font-bold opacity-60 mt-0.5">(Horizontal)</span>
                    </button>
                    <button
                      onClick={() => { setCrossbar('height'); setViewMode('full'); }}
                      className={`
                        py-3 px-3 text-sm rounded-lg border-2 transition-all flex flex-col items-center justify-center
                        ${crossbar === 'height'
                          ? 'border-[#d44a26] bg-[#d44a26]/5 text-[#d44a26] font-semibold'
                          : 'border-slate-200 hover:border-slate-300 text-slate-600 bg-white'}
                      `}
                    >
                      <span>Parallel to Height</span>
                      <span className="text-[10px] uppercase font-bold opacity-60 mt-0.5">(Vertical)</span>
                    </button>
                </div>
              </div>

              {/* Spline Roller Tool — kit only */}
              {isKit && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Kit Add-on</label>
                  <label
                    className={`
                      flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${splineTool
                        ? 'border-[#d44a26] bg-[#d44a26]/5'
                        : 'border-slate-200 hover:border-slate-300 bg-white'}
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={splineTool}
                      onChange={(e) => { setSplineTool(e.target.checked); setViewMode('full'); }}
                      className="mt-1 h-4 w-4 rounded border-slate-300 text-[#d44a26] focus:ring-[#d44a26] cursor-pointer"
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className={`text-sm font-semibold ${splineTool ? 'text-[#d44a26]' : 'text-slate-800'}`}>
                          Include Spline Roller Tool
                        </span>
                        <span className={`text-sm font-bold whitespace-nowrap ${splineTool ? 'text-[#d44a26]' : 'text-slate-600'}`}>
                          +${PRICING.splineTool.toFixed(2)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 leading-snug">
                        Needed to press the mesh and spline into the frame groove. Uncheck if you already own one.
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Comments / Custom Instructions */}
              <div 
                ref={commentsRef} 
                className={`transition-all duration-500 rounded-lg ${highlightComments ? 'ring-4 ring-[#d44a26]/30 bg-[#d44a26]/5 p-2' : ''}`}
              >
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <MessageSquare size={16} className="text-slate-500" />
                  Additional Instructions / Comments
                </label>
                <textarea
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                  placeholder={getCommentsHelper()}
                  rows={3}
                  className="block w-full rounded-lg border-slate-300 shadow-sm focus:border-[#d44a26] focus:ring-[#d44a26] sm:text-sm p-3 border bg-slate-50"
                />
                <p className="text-xs text-slate-500 mt-2 italic">
                  {getCommentsHelper()}
                </p>
              </div>

            </div>
          </section>

          {/* Order Summary — polished card below all options */}
          <div ref={orderSummaryRef} className="rounded-xl shadow-lg overflow-hidden border border-slate-200">
            {/* Your Configuration header with chips */}
            <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider mr-1">Your Build</span>
                <span className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full shadow-sm">
                  <Ruler size={12} className="text-slate-400" />
                  {totalWidth}" × {totalHeight}"
                </span>
                <span className="inline-flex items-center text-xs font-medium bg-white border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full shadow-sm capitalize">
                  {color} ({frameSize}")
                </span>
                <span className="inline-flex items-center text-xs font-medium bg-white border border-slate-200 text-slate-700 px-2.5 py-1 rounded-full shadow-sm">
                  {MESH_COLOR_LABELS[meshColor] || meshColor} {MESH_STYLES[mesh].name}
                </span>
                {hardware !== 'none' && (
                  <span className="inline-flex items-center text-xs font-medium bg-[#d44a26]/10 border border-[#d44a26]/20 text-[#d44a26] px-2.5 py-1 rounded-full">
                    {HARDWARE_OPTIONS.find(h => h.value === hardware)?.label}
                  </span>
                )}
                {crossbar !== 'none' && (
                  <span className="inline-flex items-center text-xs font-medium bg-[#d44a26]/10 border border-[#d44a26]/20 text-[#d44a26] px-2.5 py-1 rounded-full">
                    Crossbar ({crossbar === 'width' ? 'Horizontal' : 'Vertical'})
                  </span>
                )}
                {isKit && splineTool && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium bg-amber-100 border border-amber-200 text-amber-900 px-2.5 py-1 rounded-full">
                    + Spline Roller Tool
                  </span>
                )}
              </div>
            </div>

            {/* Price breakdown + CTA */}
            <div className="bg-slate-900 px-5 py-4 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#d44a26] opacity-[0.07] rounded-bl-full -mr-10 -mt-10"></div>

              {/* Mini price breakdown — only in standalone mode */}
              {!acoReady && (() => {
                const base = isKit ? PRICING_KIT.base : PRICING.base;
                const perInch = isKit ? PRICING_KIT.perUnitedInch : PRICING.perUnitedInch;
                const sizeSurcharge = (totalWidth + totalHeight) * perInch;
                return (
                  <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-400 mb-3 relative z-10">
                    <span>Base: ${base.toFixed(2)}</span>
                    <span>Size: +${sizeSurcharge > 0 ? sizeSurcharge.toFixed(2) : '0.00'}</span>
                    {(PRICING.mesh[mesh] || 0) > 0 && <span>Mesh: +${PRICING.mesh[mesh].toFixed(2)}</span>}
                    {(PRICING.hardware[hardware] || 0) > 0 && <span>Hardware: +${PRICING.hardware[hardware].toFixed(2)}</span>}
                    {crossbar !== 'none' && <span>Crossbar: +${PRICING.crossbar.toFixed(2)}</span>}
                    {isKit && splineTool && <span>Spline tool: +${PRICING.splineTool.toFixed(2)}</span>}
                  </div>
                );
              })()}

              {/* Compact Bulk Pricing Strip — assembled WSC only.
                  Kits don't qualify for bulk discount tiers or free shipping
                  (they always ship at the $12 flat-pack rate). */}
              {!isKit && (
              <div className="mb-3 relative z-10">
                {/* Encouragement / achievement line */}
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-1.5 text-[11px] text-slate-300 min-w-0">
                    <Sparkles size={11} className="text-yellow-400 shrink-0" />
                    {bulkPricing.nextMilestone ? (
                      <span className="truncate">
                        {bulkPricing.activeTier && (
                          <span className="text-green-400 font-semibold">
                            {Math.round(bulkPricing.discountPercent * 100)}% off unlocked!{' '}
                          </span>
                        )}
                        {bulkPricing.freeShipping && !bulkPricing.activeTier && (
                          <span className="text-[#f97316] font-semibold">Free shipping unlocked!{' '}</span>
                        )}
                        Add <strong className="text-white">{bulkPricing.remainingToNext}</strong> more for{' '}
                        <strong className="text-white">{bulkPricing.nextMilestone.label}</strong>
                      </span>
                    ) : (
                      <span className="text-green-400 font-semibold truncate">
                        Max savings: 15% off + FREE shipping
                      </span>
                    )}
                  </div>
                  {(bulkPricing.discountAmount > 0 || bulkPricing.freeShipping) && (
                    <span className="text-green-400 text-[11px] font-bold whitespace-nowrap shrink-0">
                      {bulkPricing.discountAmount > 0 && `−$${bulkPricing.discountAmount.toFixed(2)}`}
                      {bulkPricing.freeShipping && bulkPricing.discountAmount > 0 && ' · '}
                      {bulkPricing.freeShipping && '🚚 free'}
                    </span>
                  )}
                </div>

                {/* Progress bar with tier markers */}
                <div className="relative px-1.5">
                  <div className="relative h-1.5 bg-slate-700/70 rounded-full">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-[#d44a26] via-orange-400 to-emerald-400 transition-all duration-500 ease-out"
                      style={{ width: `${bulkPricing.progressPct}%` }}
                    />
                    {[
                      { qty: 3,  isShipping: false },
                      { qty: 5,  isShipping: false },
                      { qty: 10, isShipping: false },
                      { qty: FREE_SHIPPING_MIN_QTY, isShipping: true },
                    ].map(m => {
                      const unlocked = quantity >= m.qty;
                      return (
                        <div
                          key={m.qty}
                          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                          style={{ left: `${(m.qty / FREE_SHIPPING_MIN_QTY) * 100}%` }}
                        >
                          <div className={`w-3 h-3 rounded-full border-2 transition-all duration-300 ${
                            unlocked
                              ? (m.isShipping
                                  ? 'bg-[#f97316] border-[#f97316] shadow-[0_0_8px_rgba(249,115,22,0.6)] scale-110'
                                  : 'bg-emerald-400 border-emerald-400 shadow-[0_0_6px_rgba(52,211,153,0.5)] scale-110')
                              : 'bg-slate-700 border-slate-500'
                          }`} />
                        </div>
                      );
                    })}
                  </div>

                  {/* Tier labels under each dot */}
                  <div className="relative h-3 mt-1.5">
                    {[
                      { qty: 3,  label: '5% off',    isShipping: false },
                      { qty: 5,  label: '10% off',   isShipping: false },
                      { qty: 10, label: '15% off',   isShipping: false },
                      { qty: FREE_SHIPPING_MIN_QTY, label: 'Free Ship', isShipping: true },
                    ].map(m => {
                      const unlocked = quantity >= m.qty;
                      return (
                        <span
                          key={m.qty}
                          className={`absolute -translate-x-1/2 text-[9px] whitespace-nowrap leading-none ${
                            unlocked
                              ? (m.isShipping ? 'text-[#f97316] font-bold' : 'text-emerald-400 font-semibold')
                              : 'text-slate-500'
                          }`}
                          style={{ left: `${(m.qty / FREE_SHIPPING_MIN_QTY) * 100}%` }}
                        >
                          {m.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              )}

              {/* Quantity + Price + CTA row */}
              <div className="flex items-center gap-3 relative z-10">
                <div className="shrink-0">
                  <div className="text-slate-400 text-[10px] uppercase tracking-wider leading-none mb-1">{acoReady ? 'Estimated Total' : 'Total Price'}</div>
                  <div className="text-3xl font-bold text-white tracking-tight leading-none">
                    ${(bulkPricing.subtotal - bulkPricing.discountAmount).toFixed(2)}
                  </div>
                  {quantity > 1 && (
                    <div className="text-[10px] text-slate-400 mt-1 leading-tight space-y-0.5">
                      <div>
                        {quantity} × ${unitPrice.toFixed(2)} = ${bulkPricing.subtotal.toFixed(2)}
                      </div>
                      {bulkPricing.discountAmount > 0 && (
                        <div className="text-green-400">
                          −${bulkPricing.discountAmount.toFixed(2)} ({Math.round(bulkPricing.discountPercent * 100)}% volume discount)
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Quantity Selector */}
                <div className="flex items-center shrink-0">
                  <button
                    type="button"
                    onClick={() => setQuantity(q => Math.max(1, q - 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-l-lg bg-slate-700 hover:bg-slate-600 text-white text-lg font-bold transition-colors border border-slate-600 cursor-pointer select-none"
                    aria-label="Decrease quantity"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={quantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (!isNaN(val) && val >= 1 && val <= 99) setQuantity(val);
                    }}
                    className="w-10 h-9 text-center text-white font-bold text-sm bg-slate-800 border-y border-slate-600 appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                    style={{ MozAppearance: 'textfield' }}
                  />
                  <button
                    type="button"
                    onClick={() => setQuantity(q => Math.min(99, q + 1))}
                    className="w-9 h-9 flex items-center justify-center rounded-r-lg bg-slate-700 hover:bg-slate-600 text-white text-lg font-bold transition-colors border border-slate-600 cursor-pointer select-none"
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={async () => {
                    if (acoReady) {
                      const form =
                        document.querySelector('form[action*="/cart/add"]') ||
                        document.querySelector('.product-form');

                      // EDIT FLOW: remove the original line FIRST, BEFORE
                      // submitting the new add. Most Shopify themes redirect to
                      // /cart after the form submit, which kills any deferred
                      // setTimeout callbacks. Pre-emptive removal is the only
                      // way to guarantee the old line is gone.
                      if (editLineKey) {
                        try {
                          await fetch('/cart/change.js', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                            credentials: 'same-origin',
                            body: JSON.stringify({ id: editLineKey, quantity: 0 }),
                          });
                        } catch (e) { console.warn('[WSC] Could not remove original line:', e); }
                      }

                      // Sync quantity to the real Shopify quantity input
                      const qtyInput = (form && form.querySelector('input[name="quantity"]')) ||
                                        document.querySelector('input[name="quantity"]');
                      if (qtyInput) {
                        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
                        nativeInputValueSetter.call(qtyInput, String(quantity));
                        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
                        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
                      }

                      // Kit-only: push spline-roller-tool selection as a line item property
                      if (isKit && form) {
                        const propName = 'properties[Spline Roller Tool]';
                        let toolInput = form.querySelector(`input[name="${propName}"]`);
                        if (!toolInput) {
                          toolInput = document.createElement('input');
                          toolInput.type = 'hidden';
                          toolInput.name = propName;
                          form.appendChild(toolInput);
                        }
                        toolInput.value = splineTool
                          ? `Included (+$${PRICING.splineTool.toFixed(2)})`
                          : 'Not included';
                      }

                      // Inject `properties[_build]` so the cart page can offer
                      // an Edit button later. Underscore prefix hides it from
                      // the cart's visible property list.
                      if (form) {
                        const state = {
                          product: isKit ? 'wsc-kit' : 'wsc',
                          mesh, meshColor, frameSize, color,
                          width, widthFraction, height, heightFraction,
                          hardware, crossbar, comments,
                          splineTool: isKit ? splineTool : undefined,
                          quantity,
                        };
                        const encoded = wscEncodeBuild(state);
                        let buildInput = form.querySelector('input[name="properties[_build]"]');
                        if (!buildInput) {
                          buildInput = document.createElement('input');
                          buildInput.type = 'hidden';
                          buildInput.name = 'properties[_build]';
                          form.appendChild(buildInput);
                        }
                        buildInput.value = encoded;
                      }

                      const realBtn =
                        document.querySelector('form[action*="/cart/add"] [type="submit"]') ||
                        document.querySelector('.product-form__submit') ||
                        document.querySelector('button[name="add"]');
                      if (realBtn) {
                        realBtn.click();
                        setCartError(null);
                      } else {
                        // acoReady was true but there's no theme button to click —
                        // capture why instead of silently doing nothing.
                        captureCartFailure('no-submit-button');
                      }
                      // (Removal of the original line is done above, before the
                      // submit, so it survives the theme's potential redirect.)
                    } else {
                      // acoReady is false → the configurator never detected the
                      // ACO fields. Previously this click was a silent no-op.
                      // Now we diagnose, record, and tell the customer.
                      captureCartFailure('aco-not-ready');
                    }
                  }}
                  className="flex-1 bg-[#d44a26] hover:bg-[#b83e1e] text-white font-bold py-4 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg shadow-orange-900/30 cursor-pointer hover:shadow-xl hover:shadow-orange-900/40 active:scale-[0.98]"
                >
                  <ShoppingCart size={20} />
                  {editLineKey ? 'Save Changes' : 'Add to Cart'}
                </button>
              </div>

              {/* Add-to-Cart failure notice — only shows if a click couldn't
                  complete. Replaces the old silent no-op so customers (and we)
                  know something went wrong. Full diagnosis is in the console
                  and window.__WSC_CART_FAILURES. */}
              {cartError && (
                <div role="alert" className="mt-3 rounded-lg border border-red-400/40 bg-red-500/15 px-3 py-2 relative z-10">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-red-300 shrink-0 mt-0.5" />
                    <div className="text-[12px] leading-snug text-red-100">
                      <span className="font-semibold">Couldn’t add to cart. </span>
                      {cartError}{' '}
                      <button
                        type="button"
                        onClick={() => window.location.reload()}
                        className="underline font-semibold text-red-50 hover:text-white cursor-pointer"
                      >
                        Reload page
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Estimated savings vs the assembled version — kit only */}
              {isKit && (() => {
                // Compute the assembled bulk discount the customer would
                // qualify for at this qty (kit doesn't get one — we apply
                // it only to the assembled side of the comparison).
                const assembledTier = [...BULK_TIERS].reverse().find(t => quantity >= t.minQty) || null;
                const assembledDiscountPercent = assembledTier ? assembledTier.percent : 0;
                const savings = estimateAssembledSavings({
                  width: totalWidth,
                  height: totalHeight,
                  quantity,
                  mesh,
                  hardware,
                  crossbar,
                  kitUnitPrice: unitPrice,
                  assembledDiscountPercent,
                });
                if (savings.total <= 0) return null;
                return (
                  <div className="mt-3 rounded-lg bg-emerald-500/15 border border-emerald-400/30 px-3 py-2 relative z-10"
                       title="Estimated savings vs the assembled version of this screen. Actual savings depend on destination, carrier, current carrier rates, and quantity. Kits ship flat-packed at a flat $12 rate; assembled units ship oversized via FedEx/UPS Ground.">
                    <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 mb-1 leading-none">
                      Choose Kit, Save Money
                    </div>
                    <div className="flex flex-col gap-0.5 text-[12px] leading-tight">
                      {savings.shippingSavings > 0 && (
                        <div className="flex items-center justify-between gap-2 text-emerald-100">
                          <span className="flex items-center gap-1.5">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-300 shrink-0">
                              <rect x="1" y="3" width="15" height="13"></rect>
                              <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
                              <circle cx="5.5" cy="18.5" r="2.5"></circle>
                              <circle cx="18.5" cy="18.5" r="2.5"></circle>
                            </svg>
                            Est. Shipping Savings
                          </span>
                          <span className="font-semibold text-emerald-50">~${savings.shippingSavings.toFixed(2)}</span>
                        </div>
                      )}
                      {savings.assembledShipsFree && savings.shippingSavings === 0 && (
                        <div className="text-[10px] text-emerald-200/70 italic">
                          Note: assembled WSC also ships free at 12+ qty — the savings here are from the lower kit price.
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 text-emerald-50 font-bold border-t border-emerald-400/20 mt-0.5 pt-1">
                        <span>Total Est. Savings vs Assembled</span>
                        <span>~${savings.total.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="text-[10px] text-emerald-200/70 mt-1.5 leading-tight">
                      Estimates only — actual savings vary by destination, carrier, current rates, and quantity.
                    </div>
                  </div>
                );
              })()}

              <p className="text-center text-[10px] text-slate-500 mt-3 relative z-10">
                Custom built in the USA · Ships within {isKit ? '4' : '7'} business days
              </p>
            </div>
          </div>

        </div>

      </main>

      {/* Lightbox Modal */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
          onClick={() => setLightboxImg(null)}
        >
          <div
            className="relative max-w-3xl max-h-[85vh] mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-600 hover:text-slate-900 hover:scale-110 transition-all z-10 cursor-pointer"
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImg}
              alt={MESH_STYLES[mesh].name}
              className="rounded-xl shadow-2xl max-h-[80vh] w-auto object-contain"
              crossOrigin="anonymous"
            />
            <p className="text-center text-white/80 text-sm mt-3 font-medium">{MESH_STYLES[mesh].name}</p>
            {MESH_IMAGES[mesh] && MESH_IMAGES[mesh].length > 1 && (
              <div className="flex gap-2 justify-center mt-3">
                {MESH_IMAGES[mesh].map((imgUrl, idx) => (
                  <button
                    key={idx}
                    onClick={() => setLightboxImg(imgUrl)}
                    className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                      lightboxImg === imgUrl ? 'border-[#d44a26] shadow-md' : 'border-white/30 hover:border-white/60'
                    }`}
                  >
                    <img
                      src={imgUrl}
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                      crossOrigin="anonymous"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile-only: Floating "Preview My Screen" button — appears when preview scrolls out of view */}
      {viewportWidth < 1024 && isPreviewOutOfView && !showPreviewModal && (
        <button
          onClick={() => setShowPreviewModal(true)}
          className="fixed bottom-6 left-1/2 z-[9998] flex items-center gap-2 px-6 py-3.5 bg-[#d44a26] text-white font-bold text-sm rounded-full shadow-lg cursor-pointer transition-all duration-300 whitespace-nowrap"
          style={{
            transform: 'translateX(-50%)',
            boxShadow: '0 4px 20px rgba(212, 74, 38, 0.4), 0 2px 8px rgba(0,0,0,0.15)',
            animation: 'wsc-fab-enter 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          }}
        >
          <Eye size={18} />
          Preview My Screen
        </button>
      )}

      {/* Mobile Preview Modal */}
      {showPreviewModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => setShowPreviewModal(false)}
        >
          <div
            className="bg-white rounded-2xl w-[92%] max-w-[620px] max-h-[90vh] overflow-y-auto shadow-2xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
              <h3 className="font-bold text-slate-700 text-base">Review Your Screen</h3>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <X size={18} />
              </button>
            </div>

            {/* Preview Content */}
            <div className="p-4">
              <ScreenPreview />
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-5 py-3.5 border-t border-slate-200">
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  previewCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="px-5 py-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors cursor-pointer"
              >
                Continue Editing
              </button>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-5 py-2.5 rounded-lg bg-[#d44a26] text-white text-sm font-bold flex items-center gap-2 shadow-md hover:bg-[#b83e1e] transition-colors cursor-pointer"
              >
                <Check size={16} />
                Looks Good
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Keyframe animation for FAB entrance */}
      <style>{`
        @keyframes wsc-fab-enter {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

    </div>
  );
}
   